/* ===== Claims Management Service ===== */
import { db } from '../config/firebase.js?v=82.86';
import { $ } from '../utils/helpers.js?v=82.86';
import { showSnackbar } from '../utils/ui.js?v=82.86';
import { getCurrentUser } from './auth.js?v=82.86';
import { getCurrentSettings } from './settings.js?v=82.86';
import { normalizePhone, escapeHtml, parseFirestoreTimestamp, logFirestoreAccessDeniedOnce } from '../utils/helpers.js?v=82.86';
import { validateEmail, validatePhone } from '../utils/validation.js?v=82.86';
import { showPage } from '../ui/routing.js?v=82.86';
import { getClaimPrizeText, clearClaimPrizeText } from './game.js?v=82.86';
import { logger } from '../utils/logger.js?v=82.86';
import { submitClaimBackend, submitPublicClaimBackend, deleteAllClaimsBackend, deleteArchivedCampaignBackend, drawWinnerBackend, drawWinnersTop3Backend, purgeInvalidPendingClaims, getArchivedRunBackend } from './backend.js?v=82.86';
import { getPublicClientSlug } from './public-client.js?v=82.86';
import {
  startEventMessagesListener,
  stopEventMessagesListener,
  renderRecentEventMessages
} from './event-messages.js?v=82.86';
import { loadScriptOnce } from '../utils/lazy-loader.js?v=82.86';
import { sanitizeImageSrc } from '../utils/url.js?v=82.86';
import { runDrawCountdown, DRAW_COUNTDOWN_SECONDS } from '../utils/draw-countdown.js?v=82.86';

let claimsUnsub = null;
let lastClaimsRows = [];
let communityLeadsUnsub = null;
let lastCommunityLeadsRows = [];
let isSubmittingClaim = false;
let isDeletingArchivedCampaign = false;
let isDeletingAllClaims = false;
let isDrawingWinner = false;
let isDrawingWinnersTop3 = false;
let cachedWinnersData = null; // Cache winners data for Vizualizare modal

function normalizeReceiptNumberClient(raw) {
  return String(raw || '')
    .trim()
    .replace(/\s+/g, '')
    .toUpperCase()
    .slice(0, 80);
}

function receiptNumberClientIsValid(normalized) {
  return normalized.length >= 3 && /^[A-Z0-9\-]+$/.test(normalized);
}

async function getRequireReceiptNumberForClaim() {
  const publicSlug = getPublicClientSlug();
  if (publicSlug) {
    return !!(getCurrentSettings()?.requireReceiptNumber);
  }
  const u = getCurrentUser();
  if (!u) return false;
  if (getCurrentSettings()?.requireReceiptNumber === true) {
    return true;
  }
  try {
    const adv = await db.collection('advancedClaims').doc(u.uid).get();
    return adv.exists && adv.data()?.requireReceiptNumber === true;
  } catch (e) {
    logger.warn('[getRequireReceiptNumberForClaim]', e);
    return false;
  }
}

function bindClaimReceiptInputOnce() {
  const input = $('claimReceiptNumber');
  if (!input || input.dataset.boundReceiptNormalize) return;
  input.dataset.boundReceiptNormalize = '1';
  input.addEventListener('blur', () => {
    input.value = normalizeReceiptNumberClient(input.value);
  });
}

export async function refreshClaimReceiptFieldVisibility() {
  const wrap = $('claimReceiptWrap');
  const input = $('claimReceiptNumber');
  if (!wrap || !input) return;
  const required = await getRequireReceiptNumberForClaim();
  wrap.style.display = required ? 'block' : 'none';
  if (!required) {
    input.value = '';
    const err = $('claimReceiptError');
    if (err) {
      err.style.display = 'none';
      err.textContent = '';
    }
  }
}

export function getLastClaimsRows() {
  return lastClaimsRows;
}

export function getLastCommunityLeadsRows() {
  return lastCommunityLeadsRows;
}

export function getClaimsUnsub() {
  return claimsUnsub;
}

export function setClaimsUnsub(unsub) {
  claimsUnsub = unsub;
}

export function resetClaimsUnsub() {
  if (claimsUnsub) {
    try {
      claimsUnsub();
    } catch (e) {
      logger.error('[resetClaimsUnsub] Error unsubscribing from claims:', e);
    }
    claimsUnsub = null;
  }
}

// ✅ FIX CRITIC #4: Retry mechanism pentru onSnapshot
let claimsRetryCount = 0;
const MAX_CLAIMS_RETRIES = 5;
let claimsRetryTimeout = null;

/** Celula „marketing”: canalele acceptate și înregistrarea retragerii consimțământului. */
function marketingCell(r, td) {
  const m = r?.consent?.marketing;
  if (!m) return td(r.marketingAccepted ? '✔' : '');
  const ch = [m.email ? 'e-mail' : '', m.sms ? 'SMS' : ''].filter(Boolean);
  const withdrawn = (m.history || []).some((h) => h.action === 'withdraw');
  const cell = td(ch.length ? `✔ ${ch.join(' · ')}` : (withdrawn ? 'retras' : ''));
  if (ch.length && r._id) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'link nowrap';
    b.textContent = ' retrage';
    b.style.cssText = 'width:auto;margin:0 0 0 6px;padding:0;background:none;border:0;color:#9B2C1F;font-size:12px;text-decoration:underline;min-height:0;height:auto;box-shadow:none';
    b.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (!window.confirm('Înregistrez retragerea acordului de marketing (e-mail și SMS) pentru această persoană?')) return;
      try {
        const { withdrawMarketingConsentBackend } = await import('./backend.js?v=82.86');
        await withdrawMarketingConsentBackend(r._id, 'all');
        showSnackbar('Retragerea a fost înregistrată.', 'success');
      } catch (err) { showSnackbar(err?.message || 'Nu am putut înregistra retragerea.'); }
    });
    cell.appendChild(b);
  }
  return cell;
}

/** Campania cu documente juridice aprobate: bifele exact ca în formularul aprobat (e-mail și SMS separate). */
function applyLegalConsentLines() {
  const s = getCurrentSettings() || {};
  const gdprLabel = document.querySelector('label[for="claimGDPR"]');
  const gdprLine = $('claimGDPR')?.closest('.consent-line');
  const mktLine = $('claimMKT')?.closest('.consent-line');
  const box = gdprLine?.parentElement;
  if (!gdprLabel || !mktLine || !box) return false;
  if (!gdprLabel.dataset.orig) gdprLabel.dataset.orig = gdprLabel.innerHTML;
  return import('../legal/legal-ui.js?v=82.86').then((lg) => {
    const on = !!lg.getDisplayDoc('rules', s);
    let extra = $('claimLegalMkt');
    const detailsBtn = gdprLine.querySelector('.details-btn');
    if (!on) {
      gdprLabel.innerHTML = gdprLabel.dataset.orig;
      if (detailsBtn) detailsBtn.hidden = false;
      mktLine.hidden = false;
      if (extra) extra.hidden = true;
      return false;
    }
    const t = lg.consentTexts(lg.buildLegalContext(s));
    const esc = (x) => String(x).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    gdprLabel.innerHTML = `${esc(t.rules)
      .replace('Regulamentul oficial', '<a href="#" class="sj-lg-link" data-lg="rules">Regulamentul oficial</a>')
      .replace('informare privind prelucrarea datelor personale', '<a href="#" class="sj-lg-link" data-lg="gdpr">informare privind prelucrarea datelor personale</a>')} <em class="consent-req">obligatoriu</em>`;
    gdprLabel.querySelectorAll('a[data-lg]').forEach((a) => a.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      lg.openLegalDoc(a.dataset.lg, getCurrentSettings() || s);
    }));
    if (detailsBtn) detailsBtn.hidden = true;
    mktLine.hidden = true;
    const mk = (id, text) => `<div class="consent-line"><input id="${id}" type="checkbox"/><div class="consent-text"><label for="${id}" class="consent-label">${esc(text)} <em class="consent-opt">opțional</em></label></div></div>`;
    if (!extra) {
      extra = document.createElement('div');
      extra.id = 'claimLegalMkt';
      box.appendChild(extra);
    }
    extra.hidden = false;
    extra.innerHTML = `${mk('claimMKTEmail', t.email)}${mk('claimMKTSms', t.sms)}<p class="smallnote claim-legal-note">${esc(t.note)}</p>`;
    return true;
  }).catch(() => false);
}

function updateClaimMarketingLabel() {
  applyLegalConsentLines();
  const labelEl = $('claimMarketingLabelText');
  if (!labelEl) return;

  const companyName = String(getCurrentSettings()?.companyName || '').trim();
  const text = companyName
    ? `Vreau oferte și noutăți de la ${companyName} (opțional)`
    : 'Vreau să primesc oferte și noutăți despre promoții (opțional)';

  labelEl.textContent = text;
}

// Initialize responsibles selector in claim form (Step 1: UI + load only)
export async function initClaimResponsibleSelector() {
  try {
    updateClaimMarketingLabel();
    bindClaimReceiptInputOnce();

    const wrap = $('claimResponsibleWrap');
    const select = $('claimResponsibleSelect');
    if (!wrap || !select) return;

    // Reset to hidden/default on each entry to claim page
    wrap.style.display = 'none';
    select.innerHTML = '';
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Selectează responsabil';
    select.appendChild(defaultOption);
    select.value = '';

    const currentUser = getCurrentUser();
    const publicSlug = getPublicClientSlug();
    if (!currentUser && !publicSlug) return;

    // Mod public QR: responsabili din snapshot publicSites
    if (publicSlug) {
    const wrap = $('claimResponsibleWrap');
    const select = $('claimResponsibleSelect');
    if (!wrap || !select) return;

    wrap.style.display = 'none';
    select.innerHTML = '';
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Selectează responsabil';
    select.appendChild(defaultOption);
    select.value = '';

    const settings = getCurrentSettings();
    if (!settings?.advancedMode || !Array.isArray(settings.publicResponsibles) || settings.publicResponsibles.length === 0) {
      return;
    }

    wrap.style.display = '';
    let added = 0;
    settings.publicResponsibles.forEach((r) => {
      const id = String(r.id || '').trim();
      const name = String(r.name || '').trim();
      if (!id || !name) return;
      const option = document.createElement('option');
      option.value = id;
      option.textContent = name;
      select.appendChild(option);
      added++;
    });
    if (added === 0) {
      wrap.style.display = 'none';
    }
      return;
    }

    try {
      const advancedDoc = await db.collection('advancedClaims').doc(currentUser.uid).get();
      const mode = advancedDoc.exists ? String(advancedDoc.data()?.mode || 'standard') : 'standard';
      if (mode !== 'advanced') return;

      wrap.style.display = '';
      const respSnap = await db
        .collection('advancedClaims')
        .doc(currentUser.uid)
        .collection('responsibles')
        .orderBy('createdAt', 'asc')
        .get();

      let added = 0;
      respSnap.forEach(doc => {
        const data = doc.data() || {};
        const name = String(data.name || '').trim();
        const isActive = data.active !== false;
        if (!name || !isActive) return;

        const option = document.createElement('option');
        option.value = doc.id;
        option.textContent = name;
        select.appendChild(option);
        added++;
      });

      if (added === 0) {
        const emptyOption = document.createElement('option');
        emptyOption.value = '';
        emptyOption.textContent = 'Nu există responsabili activi';
        emptyOption.disabled = true;
        select.appendChild(emptyOption);
      }
    } catch (error) {
      logger.warn('[initClaimResponsibleSelector] Failed to load responsibles:', error);
      wrap.style.display = 'none';
    }
  } finally {
    await refreshClaimReceiptFieldVisibility();
  }
}

// Refresh claims dashboard
export async function refreshClaims() {
  // mod suport (Super Admin): fără acces la datele clienților — nu pornim ascultătorii
  if (document.body.classList.contains('sj-support-mode')) return;
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }
  
  resetClaimsUnsub();
  if (communityLeadsUnsub) {
    try {
      communityLeadsUnsub();
    } catch (_) {
      // ignore
    }
    communityLeadsUnsub = null;
  }
  stopEventMessagesListener();
  
  // Clear any pending retry
  if (claimsRetryTimeout) {
    clearTimeout(claimsRetryTimeout);
    claimsRetryTimeout = null;
  }
  
  try {
    const q = db.collection('claims').where('adminId', '==', currentUser.uid);
    claimsUnsub = q.onSnapshot((snap) => {
      // ✅ Success - reset retry count
      claimsRetryCount = 0;
      
      const rows = [];
      snap.forEach(d => {
        const r = d.data();
        /* Nu afișa orfanele pending ca revendicări confirmate */
        if (r._offlinePendingValidation === true) return;
        r._id = d.id;
        rows.push(r);
      });
      rows.sort((a, b) => String(b.date).localeCompare(String(a.date)));
      lastClaimsRows = rows;
      try { document.dispatchEvent(new CustomEvent('sj:claims-updated')); } catch (_) { /* ignore */ }
      renderClaims(lastClaimsRows);
      if (document.getElementById('allClaimsModal')?.classList.contains('active')) renderAllClaimsInModal(lastClaimsRows);

      /* Curăță pending dacă campania e inactivă (background) */
      purgeInvalidPendingClaims().catch(() => {});
      
      import('../ui/client-interface-analytics-dashboard.js?v=82.86')
        .then((m) => m.refreshClientInterfaceAnalyticsSummary?.())
        .catch(() => {});
      
      // ✅ Update closed campaigns badge
      updateClosedCampaignsBadge();
      
      // Update admin summaries when claims are loaded
      setTimeout(() => {
        if (window.updateSectionSummaries) {
          window.updateSectionSummaries();
        }
      }, 100);
    }, (err) => {
      if (logFirestoreAccessDeniedOnce('refreshClaims', err)) {
        claimsRetryCount = 0;
        return;
      }
      logger.error('[refreshClaims] Snapshot error:', err);
      showSnackbar('⚠️ Eroare Dashboard: ' + err.message, 'error');
      
      // Retry dacă nu am depășit limita
      if (claimsRetryCount < MAX_CLAIMS_RETRIES) {
        claimsRetryCount++;
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s (max 30s)
        const delay = Math.min(1000 * Math.pow(2, claimsRetryCount - 1), 30000);
        
        logger.info(`[refreshClaims] Retrying dashboard listener (attempt ${claimsRetryCount}/${MAX_CLAIMS_RETRIES}) in ${delay}ms`);
        showSnackbar(`⚠️ Reîncerc conexiune dashboard... (${claimsRetryCount}/${MAX_CLAIMS_RETRIES})`, 'warning');
        
        claimsRetryTimeout = setTimeout(() => {
          logger.info('[refreshClaims] Executing retry');
          refreshClaims(); // Recursive retry
        }, delay);
      } else {
        // Max retries reached
        logger.error('[refreshClaims] Max retries reached, giving up');
        showSnackbar('❌ Dashboard offline - reîncarcă pagina pentru a reîncerca', 'error');
        claimsRetryCount = 0; // Reset pentru viitor
      }
    });
    setClaimsUnsub(claimsUnsub);

    const qCommunity = db.collection('communityLeads').where('merchantUid', '==', currentUser.uid);
    communityLeadsUnsub = qCommunity.onSnapshot((snap) => {
      const rows = [];
      snap.forEach((d) => {
        const r = d.data() || {};
        r._id = d.id;
        rows.push(r);
      });
      rows.sort((a, b) => (claimDateObj(b.createdAt || b.date)?.getTime() || 0) - (claimDateObj(a.createdAt || a.date)?.getTime() || 0));
      lastCommunityLeadsRows = rows;
      try { document.dispatchEvent(new CustomEvent('sj:claims-updated')); } catch (_) { /* ignore */ }

      import('../ui/client-interface-analytics-dashboard.js?v=82.86')
        .then((m) => m.refreshClientInterfaceAnalyticsSummary?.())
        .catch(() => {});

      const communityModal = document.getElementById('communityLeadsModal');
      if (communityModal?.classList.contains('active')) {
        renderCommunityLeadsInModal();
      }
    }, (err) => {
      logFirestoreAccessDeniedOnce('communityLeads', err);
    });

    startEventMessagesListener();
    import('./loyalty.js?v=82.86')
      .then((m) => m.refreshLoyaltyDashboard())
      .catch(() => {});
    import('../ui/client-interface-analytics-dashboard.js?v=82.86')
      .then((m) => m.onClaimsDashboardVisible())
      .catch(() => {});
  } catch (e) {
    if (logFirestoreAccessDeniedOnce('refreshClaims', e)) {
      claimsRetryCount = 0;
      return;
    }
    logger.error('[refreshClaims] Error setting up listener:', e);
    showSnackbar('Eroare Dashboard: ' + e.message, 'error');
    
    // Retry și pentru erori de setup
    if (claimsRetryCount < MAX_CLAIMS_RETRIES) {
      claimsRetryCount++;
      const delay = Math.min(1000 * Math.pow(2, claimsRetryCount - 1), 30000);
      
      claimsRetryTimeout = setTimeout(() => {
        refreshClaims();
      }, delay);
    }
  }
}

function formatFirestoreDateShort(value) {
  const dateObj = parseFirestoreTimestamp(value);
  if (!dateObj) return '—';
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} · ${hours}:${minutes}`;
}

// Render claims table
function renderClaims(rows) {
  const q = (document.getElementById('claimSearch')?.value || '').toLowerCase();
  const body = document.getElementById('claimsBody');
  if (!body) return;
  body.innerHTML = '';
  let cnt = 0;

  rows.forEach(r => {
    // ✅ FIX: Convert Firestore Timestamp to Date correctly (once, reuse for search and display)
    let dateStr = '—';
    if (r.date) {
      try {
        // Check if it's a Firestore Timestamp (has toDate method)
        if (r.date && typeof r.date.toDate === 'function') {
          dateStr = r.date.toDate().toLocaleString('ro-RO');
        } else if (r.date && typeof r.date.toMillis === 'function') {
          // Alternative: if it has toMillis, convert via timestamp
          dateStr = new Date(r.date.toMillis()).toLocaleString('ro-RO');
        } else if (r.date.seconds) {
          // Firestore Timestamp format: { seconds, nanoseconds }
          dateStr = new Date(r.date.seconds * 1000).toLocaleString('ro-RO');
        } else {
          // Try standard Date conversion
          const dateObj = new Date(r.date);
          if (!isNaN(dateObj.getTime())) {
            dateStr = dateObj.toLocaleString('ro-RO');
          }
        }
      } catch (e) {
        logger.error('[renderClaims] Error converting date:', e, r.date);
        dateStr = '—';
      }
    }
    
    // Use converted dateStr for search
    const hay = (dateStr + ' ' + r.name + ' ' + r.contact + ' ' + (r.prize || '') + ' ' + (r.voucherCode || '') + ' ' + (r.receiptNumber || '') + ' ' + (r.gdprAccepted ? 'gdpr' : '') + ' ' + (r.marketingAccepted ? 'mkt' : '') + ' ' + (r.responsibleName || '')).toLowerCase();
    if (q && !hay.includes(q)) return;

    const tr = document.createElement('tr');

    function td(txt) {
      const cell = document.createElement('td');
      cell.style.padding = '8px 10px';
      cell.style.borderBottom = '1px solid #f1f5f9';
      cell.textContent = txt ?? '';
      return cell;
    }

    // ✅ Add row number (increment cnt first, then use it)
    cnt++;
    tr.appendChild(td(String(cnt)));
    tr.appendChild(td(dateStr));
    tr.appendChild(td(r.name || ''));
    tr.appendChild(td(r.contact || ''));
    tr.appendChild(td(`${r.prize || ''}${r.voucherCode ? ` · cod ${r.voucherCode}${r.redeemedAt ? ' ✅ folosit' : ''}` : ''}`));
    tr.appendChild(td(r.receiptNumber || '—'));
    tr.appendChild(td(r.gdprAccepted ? '✔' : ''));
    tr.appendChild(marketingCell(r, td));

    body.appendChild(tr);
  });

  const ct = document.getElementById('claimsTotal');
  if (ct) ct.textContent = String(cnt);
}

// Search listener
document.addEventListener('input', (e) => {
  if (e.target && e.target.id === 'claimSearch') {
    renderClaims(lastClaimsRows || []);
  }
});

// Submit claim
export async function submitClaim() {
  if (isSubmittingClaim) return;
  isSubmittingClaim = true;

  const currentUser = getCurrentUser();
  const publicSlug = getPublicClientSlug();
  if (!currentUser && !publicSlug) {
    isSubmittingClaim = false;
    return showSnackbar('Loghează-te!');
  }
  
  // Găsește butonul și textul original
  const btn = $('submitClaimBtn');
  const originalText = btn ? btn.textContent : 'Trimite';
  
  // Setează loading state: dezactivează butonul și schimbă textul
  if (btn) {
    btn.disabled = true;
    btn.style.pointerEvents = 'none';
    btn.classList.add('btn-disabled-state');
    btn.innerHTML = '<span class="inline-spinner"></span> Se trimite...';
  }
  
  const currentSettings = getCurrentSettings();
  const name = ($('claimName').value || '').trim();
  const contactRaw = ($('claimContact').value || '').trim();
  const gdpr = $('claimGDPR').checked;
  // Varianta cu documente aprobate: e-mail și SMS sunt opțiuni separate
  const legalMkt = $('claimLegalMkt') && !$('claimLegalMkt').hidden;
  const mktEmail = legalMkt ? $('claimMKTEmail')?.checked === true : false;
  const mktSms = legalMkt ? $('claimMKTSms')?.checked === true : false;
  const mkt = legalMkt ? (mktEmail || mktSms) : $('claimMKT').checked;
  let consentProof = null;
  if (legalMkt) {
    try {
      const lg = await import('../legal/legal-ui.js?v=82.86');
      consentProof = {
        docs: await lg.shownDocsProof(getCurrentSettings() || {}),
        rulesAccepted: !!gdpr,
        marketingEmail: mktEmail,
        marketingSms: mktSms,
        clientAt: new Date().toISOString(),
        source: publicSlug ? 'public-link' : 'kiosk'
      };
    } catch (_) { consentProof = null; }
  }
  const responsibleWrap = $('claimResponsibleWrap');
  const responsibleSelect = $('claimResponsibleSelect');
  const isAdvancedMode = !!(responsibleWrap && responsibleWrap.style.display !== 'none');
  const responsibleId = String(responsibleSelect?.value || '').trim();
  const responsibleName = String(
    responsibleSelect?.selectedOptions?.[0]?.textContent || ''
  ).trim();

  const prizeText = getClaimPrizeText();

  // Validări (la eroare, reactivează butonul)
  if (!name) {
    isSubmittingClaim = false;
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
    return showSnackbar('Completează numele.');
  }
  if (!contactRaw) {
    isSubmittingClaim = false;
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
    return showSnackbar('Completează contactul (email sau telefon).');
  }
  if (!gdpr) {
    isSubmittingClaim = false;
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
    return showSnackbar('Bifează că accepți regulamentul.');
  }

  const isEmail = validateEmail(contactRaw);
  const phoneNormalized = normalizePhone(contactRaw);
  const isPhone = validatePhone(contactRaw);

  if (!isEmail && !isPhone) {
    isSubmittingClaim = false;
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
    return showSnackbar('Contact invalid: scrie un e-mail sau un număr de telefon (ex. 0722 123 456).');
  }

  // In advanced mode, selecting a responsible is mandatory.
  if (isAdvancedMode && !responsibleId) {
    isSubmittingClaim = false;
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
    return showSnackbar('Selectează responsabilul.');
  }

  const requireReceipt = await getRequireReceiptNumberForClaim();
  let receiptNumber = null;
  const errReceiptEl = $('claimReceiptError');
  if (errReceiptEl) {
    errReceiptEl.style.display = 'none';
    errReceiptEl.textContent = '';
  }
  if (requireReceipt) {
    const rnInput = $('claimReceiptNumber');
    const normalized = normalizeReceiptNumberClient(rnInput?.value || '');
    if (rnInput) rnInput.value = normalized;
    if (!receiptNumberClientIsValid(normalized)) {
      isSubmittingClaim = false;
      if (btn) {
        btn.disabled = false;
        btn.style.pointerEvents = '';
        btn.classList.remove('btn-disabled-state');
        btn.textContent = originalText;
      }
      const msg = !normalized
        ? 'Completează numărul bonului.'
        : 'Număr bon: minim 3 caractere (litere, cifre sau cratimă).';
      if (errReceiptEl) {
        errReceiptEl.textContent = msg;
        errReceiptEl.style.display = '';
      }
      return showSnackbar(msg);
    }
    receiptNumber = normalized;
  }

  const contact = isEmail ? contactRaw.toLowerCase() : phoneNormalized;
  let claimRes = null;

  try {
    const { loadSettings } = await import('./settings.js?v=82.86');
    if (!publicSlug && currentUser) {
      await loadSettings(currentUser.uid, true);
    }
    const updatedSettings = getCurrentSettings();

    if (updatedSettings?.campaignStatus !== 'active') {
      throw new Error('Campania nu este activă');
    }
    if (!publicSlug && !String(updatedSettings?.activeCampaignId || '').trim()) {
      throw new Error('Campania nu este activă');
    }

    if (publicSlug) {
      if (!navigator.onLine) {
        throw new Error('Ai nevoie de internet pentru a trimite revendicarea.');
      }
      claimRes = await submitPublicClaimBackend(publicSlug, {
        name,
        contact,
        prize: prizeText,
        gdprAccepted: true,
        marketingAccepted: !!mkt,
        responsibleId: responsibleId || null,
        responsibleName: responsibleName || null,
        receiptNumber: requireReceipt ? receiptNumber : null,
        ticketId: window.__sjScratchTicketId || null,
        claimToken: window.__sjScratchClaimToken || null,
        consent: consentProof,
        operator: {
          companyName: updatedSettings?.companyName || null,
          cui: updatedSettings?.cui || null,
          email: updatedSettings?.operatorEmail || null
        }
      });
    } else {
      claimRes = await submitClaimBackend({
        name,
        contact,
        prize: prizeText,
        gdprAccepted: true,
        marketingAccepted: !!mkt,
        responsibleId: responsibleId || null,
        responsibleName: responsibleName || null,
        receiptNumber: requireReceipt ? receiptNumber : null,
        consent: consentProof,
        operator: {
          companyName: updatedSettings?.companyName || null,
          cui: updatedSettings?.cui || null,
          email: updatedSettings?.operatorEmail || null
        }
      });
    }

    clearClaimPrizeText();
    window.__sjScratchTicketId = null;
    window.__sjScratchClaimToken = null;

    // Verifică dacă există claimMessage și nu este gol
    const claimMsg = updatedSettings?.claimMessage;
    const hasCustomMessage = claimMsg && typeof claimMsg === 'string' && claimMsg.trim().length > 0;

    // ✅ FIX: Găsește elementele din confirmPopup, nu din confirmPage
    const confirmPopup = $('confirmPopup');
    const titleEl = confirmPopup ? confirmPopup.querySelector('#confirmCustomTitle') : $('confirmCustomTitle');
    const messageEl = confirmPopup ? confirmPopup.querySelector('#confirmCustomMessage') : $('confirmCustomMessage');
    const instructionEl = confirmPopup ? confirmPopup.querySelector('#confirmCustomInstruction') : $('confirmCustomInstruction');
    
    // Tombola: confirmarea înscrierii (fără voucher — premiile se trag la sorți la final)
    const isDrawEntry = updatedSettings?.campaignKind === 'tombola' && Array.isArray(updatedSettings?.drawPrizes) && updatedSettings.drawPrizes.length > 0;
    const dcl = updatedSettings?.campaignLegal || {};
    const dWhen = String(dcl.drawDate || '').split('-').reverse().join('.');
    if (titleEl) {
      titleEl.textContent = isDrawEntry ? '✅ Ești înscris!' : (hasCustomMessage ? '' : '🎉 Felicitări!');
    }
    if (messageEl) {
      const finalMessage = isDrawEntry
        ? `Extragerea are loc${dWhen ? ` pe ${dWhen}${dcl.drawTime ? `, ora ${dcl.drawTime}` : ''}` : ' la finalul campaniei'}. Dacă ești extras, te anunțăm la contactul lăsat. Mult noroc!`
        : hasCustomMessage
          ? claimMsg.trim()
          : 'Rezultatul tău a fost înregistrat cu succes';
      messageEl.textContent = finalMessage;
    }
    if (instructionEl) {
      // ✅ Ascunde întotdeauna instrucțiunea (nu mai este necesară)
      instructionEl.style.display = 'none';
    }

    // Apply confirmation background image to banner
    const confirmBanner = $('confirmBanner');
    if (confirmBanner) {
      const rawUrl = updatedSettings?.confirmBgURL || '';
      const safeBannerUrl = sanitizeImageSrc(rawUrl);
      if (safeBannerUrl) {
        confirmBanner.classList.remove('has-image', 'banner-loaded');
        const probe = new Image();
        probe.onload = () => {
          confirmBanner.style.backgroundImage = `url(${safeBannerUrl})`;
          confirmBanner.classList.add('has-image', 'banner-loaded');
        };
        probe.onerror = () => {
          confirmBanner.style.backgroundImage = 'none';
          confirmBanner.classList.remove('has-image');
        };
        probe.src = safeBannerUrl;
      } else {
        confirmBanner.style.backgroundImage = 'none';
        confirmBanner.classList.remove('has-image', 'banner-loaded');
      }
    }

    // Voucher digital: clientul primește un cod pe care îl arată la casă
    if (!isDrawEntry && claimRes && (claimRes.voucherCode || claimRes.claimId) && prizeText) {
      try {
        const { showVoucherAfterClaim } = await import('../ui/voucher.js?v=82.86');
        const shown = showVoucherAfterClaim({
          claimId: claimRes.claimId,
          voucherCode: claimRes.voucherCode,
          prize: prizeText,
          name,
          note: hasCustomMessage ? claimMsg.trim() : '',
          bon: (claimRes.manualBon && claimRes.manualBon.number) || window.__sjScratchManualBon || '',
          validFrom: claimRes.voucherValidFrom,
          expiresOn: claimRes.voucherExpiresOn,
          validDays: claimRes.voucherValidDays
        });
        if (shown) {
          isSubmittingClaim = false;
          try { closeClaimFormAfterVoucher(); } catch (_) { /* ignore */ }
          return;
        }
      } catch (err) {
        logger.warn('[submitClaim] voucher', err);
      }
    }

    // Show success popup instead of navigating to page
    if (confirmPopup) {
      confirmPopup.classList.add('active');
      // Auto-close after 20 seconds (un singur timer: a doua revendicare în 20 s nu mai închide confirmarea prea devreme)
      if (confirmPopupAutoCloseTimer) clearTimeout(confirmPopupAutoCloseTimer);
      confirmPopupAutoCloseTimer = setTimeout(() => {
        confirmPopupAutoCloseTimer = null;
        if (confirmPopup.classList.contains('active')) {
          closeConfirmPopup();
        }
      }, 20000);
    }
    
    // La succes: butonul rămâne dezactivat (popup-ul se deschide, formularul nu mai e vizibil)
    // Nu e nevoie să reactivezi butonul aici
    isSubmittingClaim = false;
  } catch (e) {
    logger.error('[submitClaim]', e);
    let msg = 'Eroare la trimitere';
    if (String(e && e.code || '').includes('permission') || /insufficient/i.test(String(e && e.message || ''))) {
      msg += ': Missing or insufficient permissions (reguli Firestore).';
    } else {
      msg += ': ' + (e?.message || 'necunoscută');
    }
    showSnackbar(msg);
    
    // La eroare: reactivează butonul și resetează textul
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
    isSubmittingClaim = false;
  }
}

/** Campania cu extragere: formularul e de înscriere (fără „premiu” și „voucher”). */
function isDrawEntryForm(st) {
  return !!st && st.campaignKind === 'tombola' && Array.isArray(st.drawPrizes) && st.drawPrizes.length > 0;
}

export function claimSubmitLabel(st = getCurrentSettings()) {
  return isDrawEntryForm(st) ? '🎟️ Înscrie-te gratuit' : '🎁 Primește voucherul';
}

/** Antetul formularului: „Revendică premiul” sau, la extragere, textul campaniei și „Înscrie-te”. */
export function applyClaimFormMode(st = getCurrentSettings()) {
  const page = $('claimPage');
  if (!page) return;
  const h2 = page.querySelector('.claim-head__text h2');
  const icon = page.querySelector('.claim-head__icon');
  const line = $('claimPrizeLine');
  const intro = page.querySelector('.claim-intro');
  [h2, icon, intro].forEach((el) => { if (el && el.dataset.orig === undefined) el.dataset.orig = el.textContent; });
  const draw = isDrawEntryForm(st);
  if (draw) {
    const cl = st.campaignLegal || {};
    const d = String(cl.drawDate || '').split('-').reverse().join('.');
    const title = String(cl.participationText || '').trim() || (cl.name ? `Participă la „${cl.name}”` : 'Participă la campanie');
    if (h2) h2.textContent = title;
    if (icon) icon.textContent = '🎟️';
    if (line) line.textContent = d ? `Extragerea: ${d}${cl.drawTime ? `, ora ${cl.drawTime}` : ''}` : 'Înscriere gratuită';
    if (intro) intro.textContent = 'Completează datele ca să te înscrii la extragere. Participarea este gratuită.';
  } else {
    if (h2 && h2.dataset.orig !== undefined) h2.textContent = h2.dataset.orig;
    if (icon && icon.dataset.orig !== undefined) icon.textContent = icon.dataset.orig;
    if (intro && intro.dataset.orig !== undefined) intro.textContent = intro.dataset.orig;
  }
  const btn = $('submitClaimBtn');
  if (btn && !btn.disabled) btn.textContent = claimSubmitLabel(st);
}

// Reset claim form state (button and flag)
export function resetClaimFormState() {
  isSubmittingClaim = false;
  
  const submitBtn = $('submitClaimBtn');
  if (submitBtn) {
    submitBtn.disabled = false;
    submitBtn.style.pointerEvents = '';
    submitBtn.classList.remove('btn-disabled-state');
    submitBtn.textContent = claimSubmitLabel();
  }
  try { applyClaimFormMode(); } catch (_) { /* ignore */ }
}

function closeClaimFormAfterVoucher() {
  // pagina clientului rămâne în spatele voucherului
  showPage('clientPage');
}

// Close confirmation popup
let confirmPopupAutoCloseTimer = null;
export async function closeConfirmPopup() {
  if (confirmPopupAutoCloseTimer) { clearTimeout(confirmPopupAutoCloseTimer); confirmPopupAutoCloseTimer = null; }
  const popup = $('confirmPopup');
  if (popup) {
    popup.classList.remove('active');
    // Remove escape key handler if it was attached
    if (popup.dataset.escapeHandler === 'attached') {
      // Handler was added, but we can't easily remove it without storing reference
      // The handler checks if popup is active, so it's safe to leave it
      delete popup.dataset.escapeHandler;
    }
  }
  // Navigate back to client page
  const { showPage } = await import('../ui/routing.js?v=82.86');
  showPage('clientPage');
}

// Make available globally
window.closeConfirmPopup = closeConfirmPopup;

// Export claims to Excel
export async function exportClaims() {
  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');
  
  try {
    await loadScriptOnce('https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js', 'XLSX', 8000);
    if (!window.XLSX) {
      throw new Error('XLSX nu este disponibil');
    }
    const q = db.collection('claims').where('adminId', '==', currentUser.uid);
    const snap = await q.get();
    if (snap.empty) {
      showSnackbar('Nicio revendicare');
      return;
    }

    const rows = [["Nr.", "Data", "Nume", "Contact", "Recompensă", "Număr bon", "GDPR", "Marketing", "Responsabil"]];
    let rowNum = 0;
    snap.forEach(d => {
      const c = d.data();
      let dateStr = '—';
      if (c.date) {
        try {
          if (c.date && typeof c.date.toDate === 'function') {
            dateStr = c.date.toDate().toLocaleString('ro-RO');
          } else if (c.date && typeof c.date.toMillis === 'function') {
            dateStr = new Date(c.date.toMillis()).toLocaleString('ro-RO');
          } else if (c.date.seconds) {
            dateStr = new Date(c.date.seconds * 1000).toLocaleString('ro-RO');
          } else {
            const dateObj = new Date(c.date);
            if (!isNaN(dateObj.getTime())) {
              dateStr = dateObj.toLocaleString('ro-RO');
            }
          }
        } catch (e) {
          logger.error('[exportClaims] Error converting date:', e, c.date);
        }
      }
      rowNum++;
      rows.push([
        rowNum,
        dateStr,
        c.name || "",
        c.contact || "",
        c.prize || "",
        c.receiptNumber || "",
        c.gdprAccepted ? 'DA' : 'NU',
        c.marketingAccepted ? 'DA' : 'NU',
        c.responsibleName || ""
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Revendicari");

    const filename = `revendicari_${currentUser.uid}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    a.remove();

    showSnackbar('Excel descărcat.');
  } catch (e) {
    logger.error('[exportClaims]', e);
    showSnackbar('Eroare export: ' + (e?.message || 'necunoscută'));
  }
}

export async function exportCommunityLeads() {
  const currentUser = getCurrentUser();
  if (!currentUser) return showSnackbar('Nu ești logat');
  try {
    await loadMarketingAudience(true);
    const list = getAudienceForDisplay();
    if (!list.length) return showSnackbar('Nu ai încă contacte cu acord pentru oferte');
    // coloane simple, recunoscute de Mailchimp / Brevo / Excel
    const rows = [['Contact', 'Tip', 'Sursă', 'Ultima înscriere']];
    list.forEach((r) => {
      const d = r.lastAt ? new Date(r.lastAt) : null;
      rows.push([
        r.contact || '',
        r.type === 'phone' ? 'Telefon' : 'Email',
        (r.sources || []).join('; '),
        d && !isNaN(d.getTime()) ? d.toLocaleString('ro-RO') : ''
      ]);
    });
    const csvContent = rows
      .map((row) => row.map((value) => {
        let text = String(value ?? '');
        if (/^[=+\-@]/.test(text)) text = `'${text}`;
        return `"${text.replace(/"/g, '""')}"`;
      }).join(','))
      .join('\n');
    const filename = `comunitate_oferte_${new Date().toISOString().slice(0, 10)}.csv`;
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    a.remove();
    showSnackbar(`Am exportat ${list.length} ${list.length === 1 ? 'contact' : 'contacte'}.`, 'success');
  } catch (e) {
    logger.error('[exportCommunityLeads]', e);
    showSnackbar('Nu am putut exporta lista', 'error');
  }
}

// Open delete all claims modal
export function openDeleteAllClaimsModal() {
  const modal = document.getElementById('deleteAllClaimsModal');
  if (modal) modal.classList.add('active');
}

// Close delete all claims modal
export function closeDeleteAllClaimsModal() {
  const modal = document.getElementById('deleteAllClaimsModal');
  if (modal) modal.classList.remove('active');
}

// Reset claims - opens modal instead of using window.confirm
export function resetClaims() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }
  openDeleteAllClaimsModal();
}

// Confirm and delete all claims
export async function confirmDeleteAllClaims() {
  // Protecție anti-dublu-click
  if (isDeletingAllClaims) return;
  
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }
  
  // Găsește butonul și textul original
  const btn = $('confirmDeleteAllClaimsBtn');
  const originalText = btn ? btn.textContent : 'Da, șterge revendicările';
  
  // Setează flag și dezactivează butonul
  isDeletingAllClaims = true;
  if (btn) {
    btn.disabled = true;
    btn.style.pointerEvents = 'none';
    btn.classList.add('btn-disabled-state');
    btn.innerHTML = '<span class="inline-spinner"></span> Ștergere în curs...';
  }
  
  try {
    // Use Cloud Function instead of direct Firestore delete
    await deleteAllClaimsBackend();
    
    // Close modal
    closeDeleteAllClaimsModal();
    
    // Reload claims list
    await refreshClaims();
  } catch (e) {
    logger.error('[confirmDeleteAllClaims] Error:', e);
    
    // La eroare: reactivează butonul și resetează textul
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
    
    showSnackbar('Eroare la ștergerea revendicărilor: ' + (e?.message || 'necunoscută'));
  } finally {
    // Resetează flag-ul
    isDeletingAllClaims = false;
  }
}

function syncClosedCampaignsCount(count) {
  const countEl = document.getElementById('closedCampaignsCount');
  if (countEl) countEl.textContent = String(Math.max(0, Number(count) || 0));
}

// ✅ Update closed campaigns count on dashboard (compact: „Campanii închise (X)”)
async function updateClosedCampaignsBadge() {
  const currentUser = getCurrentUser();
  if (!currentUser) return;
  
  try {
    const runsRef = db.collection('campaignArchives').doc(currentUser.uid).collection('runs');
    const snap = await runsRef.count().get();
    const count = snap.data().count || 0;
    syncClosedCampaignsCount(count);
  } catch (e) {
    logger.warn('[updateClosedCampaignsBadge] Error:', e);
  }
}

// Load archived campaigns - render in modal
export async function loadArchivedCampaigns(renderInModal = false) {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    logger.warn('[loadArchivedCampaigns] No current user');
    return;
  }
  
  // ✅ If renderInModal, use modal container; otherwise use dashboard (deprecated, kept for compatibility)
  const container = renderInModal 
    ? document.getElementById('closedCampaignsModalList')
    : document.getElementById('archivedCampaignsList');
    
  // ✅ FIX: Error handling mai explicit dacă container-ul lipsește
  if (!container) {
    const errorMsg = renderInModal 
      ? 'Containerul modalului nu a fost găsit (closedCampaignsModalList)'
      : 'Containerul dashboard-ului nu a fost găsit (archivedCampaignsList)';
    logger.error('[loadArchivedCampaigns]', errorMsg);
    showSnackbar('Eroare: Containerul nu a fost găsit', 'error');
    throw new Error(errorMsg);
  }
  
  try {
    const runsRef = db.collection('campaignArchives').doc(currentUser.uid).collection('runs');
    let snap;
    try {
      // Try with orderBy first (requires index)
      snap = await runsRef.orderBy('closedAt', 'desc').get();
    } catch (orderByError) {
      // ✅ FIX: Fallback if index is missing - get all and sort in memory
      if (orderByError?.code === 'failed-precondition') {
        logger.warn('[loadArchivedCampaigns] Index missing for orderBy, falling back to in-memory sort');
        snap = await runsRef.get();
      } else {
        throw orderByError; // Re-throw if it's a different error
      }
    }
    
    const runs = [];
    snap.forEach(doc => {
      runs.push({ id: doc.id, ...doc.data() });
    });
    
    // ✅ FIX: Sort in memory if orderBy failed (fallback)
    if (runs.length > 0 && !snap.query.orderBy) {
      runs.sort((a, b) => {
        const aTime = a.closedAt?.toMillis?.() || a.closedAt?.seconds * 1000 || (a.closedAt ? new Date(a.closedAt).getTime() : 0);
        const bTime = b.closedAt?.toMillis?.() || b.closedAt?.seconds * 1000 || (b.closedAt ? new Date(b.closedAt).getTime() : 0);
        return bTime - aTime; // Descending order
      });
    }
    
    if (runs.length === 0) {
      container.innerHTML = '<div class="smallnote" style="text-align: center; padding: var(--space-lg); color: var(--text-secondary);">Nicio campanie închisă</div>';
      if (!renderInModal) {
        syncClosedCampaignsCount(0);
      }
      return;
    }
    
    const toD = (v) => { const d = claimDateObj(v); return d; };
    const dm = (d) => d ? d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' }) : '';
    const dmy = (d) => d ? d.toLocaleDateString('ro-RO') : '—';
    let pendingDraws = 0;
    const ICONS = {
      draw: '<path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z"/><path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3"/>',
      scratch: '<path d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z"/><path d="M14 5v12" stroke-dasharray="2 2"/>',
      game: '<rect x="3" y="8" width="18" height="13" rx="2"/><path d="M12 8v13M3 12h18M12 8c-2-4-6-4-6-1.5S10 8 12 8zm0 0c2-4 6-4 6-1.5S14 8 12 8z"/>'
    };
    container.innerHTML = runs.map(run => {
      const s = run.snapshotSettings || {};
      const stats = run.finalStats || {};
      const safeRunId = escapeHtml(String(run.id || ''));
      const kind = s.campaignKind === 'scratch' ? 'scratch' : (s.campaignKind === 'tombola' ? 'draw' : 'game');
      const start = toD(run.startedAt); const end = toD(run.closedAt);
      const sameDay = start && end && dmy(start) === dmy(end);
      const period = !start ? dmy(end) : (sameDay ? dmy(end) : `${dm(start)} – ${dmy(end)}`);
      const kindLabel = kind === 'scratch' ? 'Răzuiește și câștigă' : (kind === 'draw' ? 'Extragere' : 'Joc cu premii');
      const title = String(s.campaignTitle || s.campaignName || '').trim() || kindLabel;
      const claimsN = stats.claimsCount || 0;
      const claimsWord = kind === 'draw' ? (claimsN === 1 ? 'înscriere' : 'înscrieri') : (claimsN === 1 ? 'revendicare' : 'revendicări');
      const drawAt = run.tombolaDraw?.at || run.winnersSelectedAt || run.winnerSelectedAt || null;
      const drawn = !!(run.tombolaDraw || (Array.isArray(run.winners) && run.winners.length) || run.winnerSelected === true);
      const needsDraw = kind === 'draw' && !drawn;
      if (needsDraw) pendingDraws++;
      const sub = kind === 'scratch'
        ? `${period} · ${stats.attemptsUsed || 0} din ${stats.attemptsTotal || 0} jucate · ${claimsN} ${claimsN === 1 ? 'premiu' : 'premii'}`
        : `${period} · ${claimsN} ${claimsWord}`;
      const prizesN = Array.isArray(s.drawPrizes) ? s.drawPrizes.length : 0;
      const subShown = needsDraw && prizesN ? `${period} · ${kindLabel}` : sub;

      let action = '';
      if (kind === 'scratch') {
        action = `<button type="button" class="sjk-mini" data-action="downloadScratchDeck" data-arg="${safeRunId}"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M5 21h14"/></svg>Lista biletelor</button>`;
      } else if (needsDraw) {
        action = `${prizesN ? `<div class="sjk-nums"><span><b>${claimsN}</b>${claimsWord}</span><span><b>${prizesN}</b>${prizesN === 1 ? 'premiu' : 'premii'}</span></div>` : ''}
          <button type="button" class="sjk-primary" data-action="openWinnerDraw" data-arg="${safeRunId}">Extrage câștigătorii</button>`;
      } else if (drawn) {
        const when = drawAt ? ` · ${escapeHtml(dm(toD(drawAt)))}` : '';
        action = `<div class="sjk-row">
            <span class="sjk-done"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>Câștigători extrași${when}</span>
            <button type="button" class="sjk-mini" data-action="openWinnerDraw" data-arg="${safeRunId}">Extragerea</button>
          </div>`;
      } else {
        action = `<button type="button" class="sjk-mini" data-action="openWinnerDraw" data-arg="${safeRunId}">Extrage câștigători</button>`;
      }

      return `
        <div class="sjk-card${needsDraw ? ' is-pending' : ''}">
          <button type="button" class="sjk-main" data-action="viewArchivedCampaign" data-arg="${safeRunId}" aria-label="Deschide ${escapeHtml(title)}">
            <span class="sjk-ico sjk-ico--${kind}"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[kind]}</svg></span>
            <span class="sjk-text">
              <b class="sjk-title">${escapeHtml(title)}</b>
              <span class="sjk-sub">${escapeHtml(subShown)}</span>
            </span>
            <svg class="sjk-chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
          </button>
          ${action}
        </div>
      `;
    }).join('');
    const closedSub = document.getElementById('closedCampaignsSub');
    if (closedSub) closedSub.textContent = `${runs.length} ${runs.length === 1 ? 'campanie' : 'campanii'}${pendingDraws ? ` · ${pendingDraws} ${pendingDraws === 1 ? 'așteaptă' : 'așteaptă'} extragerea` : ''}`;
    
    // ✅ Update badge count on dashboard (if not rendering in modal)
    if (!renderInModal) {
      syncClosedCampaignsCount(runs.length);
    }
  } catch (e) {
    logger.error('[loadArchivedCampaigns] Error:', e);
    // ✅ FIX: Show more detailed error message for debugging
    const errorMsg = e?.message || e?.code || 'necunoscută';
    const errorDetails = e?.code === 'failed-precondition' 
      ? ' (Index Firestore lipsă - verifică consola pentru detalii)' 
      : '';
    container.innerHTML = `<div class="smallnote" style="text-align: center; padding: var(--space-lg); color: var(--error);">Eroare la încărcarea campaniilor arhivate: ${escapeHtml(errorMsg)}${errorDetails}</div>`;
    console.error('[loadArchivedCampaigns] Full error:', e);
  }
}

// ✅ Open closed campaigns modal
export function openClosedCampaignsModal() {
  const modal = document.getElementById('closedCampaignsModal');
  const container = document.getElementById('closedCampaignsModalList');
  
  // ✅ FIX: Verifică atât modal-ul cât și container-ul
  if (!modal) {
    logger.error('[openClosedCampaignsModal] Modal not found');
    showSnackbar('Eroare: Modalul nu a fost găsit', 'error');
    return;
  }
  
  if (!container) {
    logger.error('[openClosedCampaignsModal] Container not found - DOM may not be ready');
    showSnackbar('Eroare: Se încarcă... Încearcă din nou', 'error');
    // ✅ FIX: Retry după un scurt delay dacă container-ul nu există
    setTimeout(() => {
      const retryContainer = document.getElementById('closedCampaignsModalList');
      if (retryContainer) {
        openClosedCampaignsModal(); // Retry
      } else {
        showSnackbar('Eroare: Containerul nu a fost găsit', 'error');
      }
    }, 100);
    return;
  }
  
  // ✅ FIX: Afișează loading state imediat
  container.innerHTML = '<div class="smallnote" style="text-align: center; padding: var(--space-lg); color: var(--text-secondary);">Se încarcă...</div>';
  
  // ✅ FIX: Adaugă modal-ul activ înainte de a încărca datele
  modal.classList.add('active');
  
  // ✅ FIX: Încarcă datele cu error handling explicit
  loadArchivedCampaigns(true).catch((error) => {
    logger.error('[openClosedCampaignsModal] Error loading campaigns:', error);
    const errorMsg = error?.message || 'necunoscută';
    container.innerHTML = `<div class="smallnote" style="text-align: center; padding: var(--space-lg); color: var(--error);">Eroare la încărcarea campaniilor: ${escapeHtml(errorMsg)}</div>`;
    showSnackbar('Eroare la încărcarea campaniilor închise', 'error');
  });
}

// ✅ Close closed campaigns modal
export function closeClosedCampaignsModal() {
  const modal = document.getElementById('closedCampaignsModal');
  if (modal) modal.classList.remove('active');
}

/**
 * Citirea unei campanii arhivate (+ înscrierile) nu trebuie să rămână la „Se încarcă…” la nesfârșit.
 * Pe telefon, citirea Firestore din browser poate rămâne agățată (WebChannel după ce aplicația a stat
 * în fundal, rețea mobilă, tab principal înghețat). După ARCHIVE_READ_TIMEOUT_MS fără răspuns, citim
 * aceleași date prin funcția HTTPS getArchivedRun (conexiune nouă), apoi lăsăm eroarea să ajungă la UI.
 */
const ARCHIVE_READ_TIMEOUT_MS = 6000;

function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(Object.assign(new Error('Firestore nu a răspuns în timp util'), { code: 'sj/timeout' })), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

async function loadArchivedRun(uid, runId) {
  const runRef = db.collection('campaignArchives').doc(uid).collection('runs').doc(runId);
  try {
    const runDoc = await withTimeout(runRef.get(), ARCHIVE_READ_TIMEOUT_MS);
    if (!runDoc.exists) return { found: false };
    const claimsSnap = await withTimeout(runRef.collection('claims').orderBy('date', 'desc').get(), ARCHIVE_READ_TIMEOUT_MS);
    const claims = [];
    claimsSnap.forEach((doc) => { claims.push({ id: doc.id, ...doc.data() }); });
    return { found: true, run: runDoc.data() || {}, claims };
  } catch (e) {
    if (e?.code !== 'sj/timeout') throw e;
    logger.warn('[loadArchivedRun] Firestore nu a răspuns în', ARCHIVE_READ_TIMEOUT_MS, 'ms; citesc prin server');
    const r = await getArchivedRunBackend(runId);
    if (!r || !r.found) return { found: false };
    return { found: true, run: r.run || {}, claims: Array.isArray(r.claims) ? r.claims : [], viaServer: true };
  }
}

function archiveLoadErrorHtml(e, retryId) {
  const slow = e?.code === 'sj/timeout' || /deadline|unavailable|network|timp util/i.test(String(e?.message || ''));
  return `<div class="smallnote" style="text-align: center; padding: var(--space-lg); color: var(--error);">${slow ? 'Conexiunea nu răspunde. Datele nu au putut fi încărcate.' : 'Eroare la încărcarea datelor'}</div>
    <div style="text-align:center"><button type="button" class="btn-ghost" id="${retryId}" style="min-width:200px">🔄 Reîncearcă</button></div>`;
}

// View archived campaign details
export async function viewArchivedCampaign(runId) {
  const currentUser = getCurrentUser();
  if (!currentUser) return;

  const modal = document.getElementById('archivedCampaignViewModal');
  const titleEl = document.getElementById('archivedCampaignTitle');
  const contentEl = document.getElementById('archivedCampaignContent');

  if (!modal || !titleEl || !contentEl) return;

  // Store runId for delete operation
  currentArchivedRunId = runId;

  try {
    contentEl.innerHTML = '<div class="smallnote" style="text-align: center; padding: var(--space-lg);">Se încarcă...</div>';
    modal.classList.add('active');

    const loaded = await loadArchivedRun(currentUser.uid, runId);

    if (!loaded.found) {
      contentEl.innerHTML = '<div class="smallnote" style="text-align: center; padding: var(--space-lg); color: var(--error);">Campania nu a fost găsită</div>';
      return;
    }

    const run = loaded.run;
    const stats = run.finalStats || {};
    const startDate = run.startedAt ? new Date(run.startedAt).toLocaleString('ro-RO') : '—';
    const endDate = run.closedAt ? new Date(run.closedAt).toLocaleString('ro-RO') : '—';

    // Load archived claims
    const claims = loaded.claims;

    titleEl.textContent = 'Campanie arhivată';
    const archSub = document.getElementById('archivedCampaignSub');
    if (archSub) archSub.textContent = `${startDate.replace(/:\d{2}$/, '')} → ${endDate.replace(/:\d{2}$/, '')}`;
    const archDraw = run.campaignKind === 'tombola' || (Array.isArray(run.drawPrizes) && run.drawPrizes.length > 0) || (Array.isArray(run.settings?.drawPrizes) && run.settings.drawPrizes.length > 0);
    const exportBtnPre = document.getElementById('archivedExportBtn');
    if (exportBtnPre) exportBtnPre.hidden = true;
    
    contentEl.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: var(--space-lg);">
        ${(() => {
          // ✅ FIX: Check Top 3 winners FIRST (they take priority)
          if (run.winners && Array.isArray(run.winners) && run.winners.length > 0) {
            const selectedAt = run.winnersSelectedAt ? new Date(run.winnersSelectedAt).toLocaleString('ro-RO') : '—';
            return `
            <div class="card" style="background: linear-gradient(135deg, #1f8f4a, #1e7f45); color: white; padding: var(--space-lg); text-align: center;">
              <h4 style="margin-bottom: var(--space-md); color: white;">🎉 Câștigătorii campaniei</h4>
              ${run.winners.map((winner, idx) => {
                const place = idx + 1;
                const placeEmoji = place === 1 ? '🥇' : place === 2 ? '🥈' : '🥉';
                // ✅ Găsește claim-ul câștigătorului pentru datele complete (ne-anonimizate)
                const winnerClaim = claims.find(c => c.id === winner.claimId);
                const winnerContact = winnerClaim ? winnerClaim.contact : (winner.maskedContact || '—');
                const winnerName = winnerClaim ? winnerClaim.name : (winner.maskedName || '—');
                
                return `
                <div style="margin-bottom: ${idx < run.winners.length - 1 ? 'var(--space-lg)' : '0'}; padding-bottom: ${idx < run.winners.length - 1 ? 'var(--space-lg)' : '0'}; border-bottom: ${idx < run.winners.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none'};">
                  <div style="font-size: var(--font-size-base); margin-bottom: var(--space-xs); opacity: 0.9;">
                    ${placeEmoji} Locul ${place}
                  </div>
                  <div style="font-size: var(--font-size-lg); margin-bottom: var(--space-sm);">
                    <strong>${escapeHtml(winnerContact)}</strong>
                  </div>
                  ${winnerName ? `<div style="font-size: var(--font-size-base); margin-bottom: var(--space-xs); opacity: 0.9;">${escapeHtml(winnerName)}</div>` : ''}
                  ${winner.prize ? `<div style="font-size: var(--font-size-sm); opacity: 0.9; margin-top: var(--space-xs);">Recompensă: ${escapeHtml(winner.prize)}</div>` : ''}
                </div>
              `;
              }).join('')}
              <div style="font-size: var(--font-size-xs); opacity: 0.8; margin-top: var(--space-md); padding-top: var(--space-md); border-top: 1px solid rgba(255,255,255,0.2);">
                Extrași la: ${selectedAt}
              </div>
            </div>
          `;
          }
          // Check for single winner (only if Top 3 doesn't exist)
          else if (run.winnerSelected === true && run.winnerClaimId) {
            // ✅ Găsește claim-ul câștigătorului pentru datele complete (ne-anonimizate)
            const winnerClaim = claims.find(c => c.id === run.winnerClaimId);
            const winnerContact = winnerClaim ? winnerClaim.contact : (run.winnerMaskedContact || '—');
            const winnerName = winnerClaim ? winnerClaim.name : (run.winnerMaskedName || '—');
            
            return `
            <div class="card" style="background: linear-gradient(135deg, #1f8f4a, #1e7f45); color: white; padding: var(--space-lg); text-align: center;">
              <h4 style="margin-bottom: var(--space-md); color: white;">🎉 Câștigător campanie</h4>
              <div style="font-size: var(--font-size-lg); margin-bottom: var(--space-sm);">
                <strong>${escapeHtml(winnerContact)}</strong>
              </div>
              ${winnerName ? `<div style="font-size: var(--font-size-base); margin-bottom: var(--space-sm); opacity: 0.9;">${escapeHtml(winnerName)}</div>` : ''}
              <div style="font-size: var(--font-size-sm); opacity: 0.9;">
                Data revendicării: ${(() => {
                  let claimDateStr = '—';
                  if (run.winnerClaimDate) {
                    try {
                      if (run.winnerClaimDate && typeof run.winnerClaimDate.toDate === 'function') {
                        claimDateStr = run.winnerClaimDate.toDate().toLocaleString('ro-RO');
                      } else if (run.winnerClaimDate && typeof run.winnerClaimDate.toMillis === 'function') {
                        claimDateStr = new Date(run.winnerClaimDate.toMillis()).toLocaleString('ro-RO');
                      } else if (run.winnerClaimDate.seconds) {
                        claimDateStr = new Date(run.winnerClaimDate.seconds * 1000).toLocaleString('ro-RO');
                      } else {
                        const dateObj = new Date(run.winnerClaimDate);
                        if (!isNaN(dateObj.getTime())) {
                          claimDateStr = dateObj.toLocaleString('ro-RO');
                        }
                      }
                    } catch (e) {
                      logger.error('[viewArchivedCampaign] Error converting winner claim date:', e, run.winnerClaimDate);
                      claimDateStr = '—';
                    }
                  }
                  return claimDateStr;
                })()}
              </div>
              ${run.winnerPrize ? `<div style="font-size: var(--font-size-sm); opacity: 0.9; margin-top: var(--space-xs);">Recompensă: ${escapeHtml(run.winnerPrize)}</div>` : ''}
              ${run.winnerSelectedAt ? `<div style="font-size: var(--font-size-xs); opacity: 0.8; margin-top: var(--space-md); padding-top: var(--space-md); border-top: 1px solid rgba(255,255,255,0.2);">Extras la: ${new Date(run.winnerSelectedAt).toLocaleString('ro-RO')}</div>` : ''}
            </div>
          `;
          }
          return '';
        })()}
        
        <div class="card sja-stats">
          <div class="sja-stats__grid">
            <div class="sja-stat"><b>${stats.claimsCount || 0}</b><span>${archDraw ? 'Înscrieri' : 'Revendicări'}</span></div>
            <div class="sja-stat"><b>${stats.winsCount || 0}</b><span>Câștiguri</span></div>
            <div class="sja-stat"><b>${stats.attemptsUsed || 0}</b><span>Încercări folosite</span></div>
            <div class="sja-stat"><b>${stats.attemptsRemaining || 0}</b><span>din ${stats.attemptsTotal || 0} rămase</span></div>
            <div class="sja-stat"><b>${stats.prizesInPlay || 0}</b><span>Recompense în joc</span></div>
            <div class="sja-stat"><b>${stats.prizesAvailable || 0}</b><span>Recompense disponibile</span></div>
          </div>
        </div>

        <div class="sja-list-head">
          <span>${archDraw ? 'Înscrieri' : 'Revendicări'}</span>
          <span class="sjw-chip">${claims.length}</span>
        </div>
        <div id="archivedClaimsList" class="sjc-list sjc-list--static"></div>
      </div>
    `;
    const archList = document.getElementById('archivedClaimsList');
    let archOpen = null;
    const renderArch = () => renderClaimCards(archList, claims, {
      draw: archDraw,
      getOpen: () => archOpen,
      setOpen: (v) => { archOpen = v; },
      rerender: renderArch,
      emptyText: archDraw ? 'Nicio înscriere' : 'Nicio revendicare'
    });
    if (archList) renderArch();
    const exportBtn = document.getElementById('archivedExportBtn');
    if (exportBtn) {
      exportBtn.dataset.arg = runId;
      exportBtn.hidden = claims.length === 0;
    }
  } catch (e) {
    logger.error('[viewArchivedCampaign] Error:', e);
    contentEl.innerHTML = archiveLoadErrorHtml(e, 'archivedCampaignRetry');
    document.getElementById('archivedCampaignRetry')?.addEventListener('click', () => viewArchivedCampaign(runId));
  }
}

// Store current runId for delete operation
let currentArchivedRunId = null;

export function closeArchivedCampaignView() {
  const modal = document.getElementById('archivedCampaignViewModal');
  if (modal) modal.classList.remove('active');
  // Reset stored runId when closing
  currentArchivedRunId = null;
}

// Open delete archived campaign modal
export function openDeleteArchivedCampaignModal() {
  // Get runId from the current view
  const modal = document.getElementById('archivedCampaignViewModal');
  if (!modal || !modal.classList.contains('active')) {
    return showSnackbar('Eroare: campania nu este încărcată');
  }
  
  // Try to get runId from the content (we need to store it when opening the view)
  if (!currentArchivedRunId) {
    return showSnackbar('Eroare: ID campanie lipsă');
  }
  
  const deleteModal = document.getElementById('deleteArchivedCampaignModal');
  if (deleteModal) {
    // ✅ FIX: Reset button state when opening modal
    const btn = $('confirmDeleteArchivedCampaignBtn');
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = 'Da, șterge campania';
    }
    // Reset flag in case it was stuck
    isDeletingArchivedCampaign = false;
    
    deleteModal.classList.add('active');
  }
}

// Close delete archived campaign modal
export function closeDeleteArchivedCampaignModal() {
  const modal = document.getElementById('deleteArchivedCampaignModal');
  if (modal) {
    modal.classList.remove('active');
    
    // ✅ FIX: Reset button state when closing modal
    const btn = $('confirmDeleteArchivedCampaignBtn');
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = 'Da, șterge campania';
    }
    // Reset flag when closing
    isDeletingArchivedCampaign = false;
  }
}

// Confirm and delete archived campaign
export async function confirmDeleteArchivedCampaign() {
  // Protecție anti-dublu-click
  if (isDeletingArchivedCampaign) return;
  
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }
  
  if (!currentArchivedRunId) {
    return showSnackbar('Eroare: ID campanie lipsă');
  }
  
  // Găsește butonul și textul original
  const btn = $('confirmDeleteArchivedCampaignBtn');
  const originalText = btn ? btn.textContent : 'Da, șterge campania';
  
  // Setează flag și dezactivează butonul
  isDeletingArchivedCampaign = true;
  if (btn) {
    btn.disabled = true;
    btn.style.pointerEvents = 'none';
    btn.classList.add('btn-disabled-state');
    btn.innerHTML = '<span class="inline-spinner"></span> Ștergere în curs...';
  }
  
  try {
    // Use Cloud Function instead of direct Firestore writes
    await deleteArchivedCampaignBackend(currentArchivedRunId);
    
    // Close modals
    closeDeleteArchivedCampaignModal();
    closeArchivedCampaignView();
    
    // Reset stored runId
    currentArchivedRunId = null;
    
    // ✅ FIX: Reload archived campaigns list doar dacă modalul este deschis
    const modal = document.getElementById('closedCampaignsModal');
    const container = document.getElementById('closedCampaignsModalList');
    
    if (modal && container && modal.classList.contains('active')) {
      // Modalul este deschis, reîncarcă lista în modal
      if (typeof window.loadArchivedCampaigns === 'function') {
        await window.loadArchivedCampaigns(true);
      }
    } else {
      // Modalul nu este deschis, doar actualizează badge-ul
      if (window.updateClosedCampaignsBadge) {
        await window.updateClosedCampaignsBadge();
      }
    }
  } catch (e) {
    logger.error('[confirmDeleteArchivedCampaign] Error:', e);
    
    // La eroare: reactivează butonul și resetează textul
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
    
    showSnackbar('Eroare la ștergere: ' + (e?.message || 'necunoscută'));
  } finally {
    // Resetează flag-ul
    isDeletingArchivedCampaign = false;
  }
}

// Export archived campaign claims to CSV
export async function exportArchivedCampaignClaims(runId) {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }
  
  try {
    const loaded = await loadArchivedRun(currentUser.uid, runId);
    
    if (!loaded.found) {
      return showSnackbar('Campania nu a fost găsită');
    }
    
    const run = loaded.run;
    const exportClaims = loaded.claims;
    
    if (!exportClaims.length) {
      return showSnackbar('Nicio revendicare de exportat');
    }
    
    // Build CSV content (using ; separator for Excel RO compatibility)
    const headers = ['nr', 'data', 'nume', 'contact', 'recompensă', 'număr bon', 'gdprAccepted', 'marketingAccepted', 'responsabil'];
    const rows = [headers.join(';')];
    
    let rowNum = 0;
    exportClaims.forEach((claim) => {
      rowNum++;
      
      // ✅ FIX: Convert Firestore Timestamp to Date correctly
      let dateStr = '';
      if (claim.date) {
        try {
          if (claim.date && typeof claim.date.toDate === 'function') {
            dateStr = claim.date.toDate().toLocaleString('ro-RO');
          } else if (claim.date && typeof claim.date.toMillis === 'function') {
            dateStr = new Date(claim.date.toMillis()).toLocaleString('ro-RO');
          } else if (claim.date.seconds) {
            dateStr = new Date(claim.date.seconds * 1000).toLocaleString('ro-RO');
          } else {
            const dateObj = new Date(claim.date);
            if (!isNaN(dateObj.getTime())) {
              dateStr = dateObj.toLocaleString('ro-RO');
            }
          }
        } catch (e) {
          logger.error('[exportArchivedCampaignClaims] Error converting date:', e, claim.date);
          dateStr = '';
        }
      }
      
      const row = [
        rowNum,
        dateStr,
        escapeCsvField(claim.name || '', ';'),
        escapeCsvField(claim.contact || '', ';'),
        escapeCsvField(claim.prize || '', ';'),
        escapeCsvField(claim.receiptNumber || '', ';'),
        claim.gdprAccepted ? 'DA' : 'NU',
        claim.marketingAccepted ? 'DA' : 'NU',
        escapeCsvField(claim.responsibleName || '', ';')
      ];
      rows.push(row.join(';'));
    });
    
    const csvContent = rows.join('\n');
    
    // Generate filename with date
    const closedDate = run.closedAt ? new Date(run.closedAt).toLocaleDateString('ro-RO').replace(/\//g, '-') : 'unknown';
    const filename = `campanie_${closedDate}_revendicari.csv`;
    
    // Create blob and trigger download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel UTF-8
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
    
    showSnackbar('CSV descărcat ✔');
  } catch (e) {
    logger.error('[exportArchivedCampaignClaims] Error:', e);
    showSnackbar('Eroare la export: ' + e.message);
  }
}

// Helper function to escape CSV fields
function escapeCsvField(value, separator = ';') {
  const str = String(value || '');
  // If field contains separator, quote, or newline, wrap in quotes and escape quotes
  if (str.includes(separator) || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

// Helper function to mask name
function maskName(name) {
  if (!name || name.length === 0) return '—';
  if (name.length <= 2) return name[0] + '*';
  return name[0] + '*'.repeat(Math.min(name.length - 1, 3));
}

// Helper function to mask contact
function maskContact(contact) {
  if (!contact) return '—';
  
  // Check if it's an email
  if (contact.includes('@')) {
    const [local, domain] = contact.split('@');
    if (!domain) return contact;
    const [domainName, ...tldParts] = domain.split('.');
    const tld = tldParts.join('.');
    
    const maskedLocal = local.length > 2 ? local.substring(0, 2) + '*'.repeat(Math.min(local.length - 2, 3)) : local[0] + '*';
    const maskedDomain = domainName.length > 2 ? domainName.substring(0, 2) + '*'.repeat(Math.min(domainName.length - 2, 3)) : domainName[0] + '*';
    
    return `${maskedLocal}@${maskedDomain}.${tld}`;
  }
  
  // Phone number
  const digits = contact.replace(/\D/g, '');
  if (digits.length === 0) return contact;
  
  if (digits.length >= 10) {
    return digits.substring(0, 2) + '*'.repeat(2) + ' ' + '*'.repeat(3) + ' ' + '*'.repeat(2) + digits.substring(digits.length - 2);
  }
  
  return '*'.repeat(Math.min(digits.length, 6));
}

// Open winner draw modal
export async function openWinnerDraw(runId) {
  const currentUser = getCurrentUser();
  if (!currentUser) return;
  
  const modal = document.getElementById('winnerDrawModal');
  const titleEl = document.getElementById('winnerDrawTitle');
  const contentEl = document.getElementById('winnerDrawContent');
  
  if (!modal || !titleEl || !contentEl) return;
  
  try {
    contentEl.innerHTML = '<div class="smallnote" style="text-align: center; padding: var(--space-lg);">Se încarcă...</div>';
    modal.classList.add('active');
    
    const loaded = await loadArchivedRun(currentUser.uid, runId);
    
    if (!loaded.found) {
      contentEl.innerHTML = '<div class="smallnote" style="text-align: center; padding: var(--space-lg); color: var(--error);">Campania nu a fost găsită</div>';
      return;
    }
    
    const run = loaded.run;
    const stats = run.finalStats || {};
    const startDate = run.startedAt ? new Date(run.startedAt).toLocaleDateString('ro-RO') : '—';
    const endDate = run.closedAt ? new Date(run.closedAt).toLocaleDateString('ro-RO') : '—';
    
    // Load archived claims
    const allClaims = loaded.claims;
    
    // Tombola cu premii pe locuri: ecranul nou de extragere (locuri, rezerve, proces-verbal, publicare)
    const snapS = run.snapshotSettings || {};
    if (snapS.campaignKind === 'tombola' && Array.isArray(snapS.drawPrizes) && snapS.drawPrizes.length) {
      const m = await import('../legal/legal-draw.js?v=82.86');
      m.renderDrawPanel({ runId, run, claims: allClaims, contentEl, titleEl });
      return;
    }

    // Get default prize message from snapshot settings
    const defaultPrizeMsg = run.snapshotSettings?.defaultPrizeMsg || 'Ne pare rău, nu ai câștigat!';
    
    // Filter winners (claims with prize that is not default message)
    const winners = allClaims.filter(claim => {
      return claim.prize && claim.prize !== defaultPrizeMsg && claim.prize.trim() !== '';
    });
    
    // Check if winner already selected
    const winnerSelected = run.winnerSelected === true;
    const existingWinner = run.winnerClaimId ? winners.find(w => w.id === run.winnerClaimId) : null;
    
    titleEl.textContent = `Extragere câștigător – ${endDate}`;
    
    let resultHtml = '';
    if (winnerSelected && existingWinner) {
      // Show existing winner - extraction is locked
      const selectedAt = run.winnerSelectedAt ? new Date(run.winnerSelectedAt).toLocaleString('ro-RO') : '—';
      resultHtml = `
        <div class="card" style="background: linear-gradient(135deg, #1f8f4a, #1e7f45); color: white; padding: var(--space-xl); text-align: center; margin-bottom: var(--space-lg);">
          <h3 style="margin-bottom: var(--space-md); color: white;">🎉 Câștigătorul a fost extras!</h3>
          <div style="font-size: var(--font-size-lg); margin-bottom: var(--space-sm);">
            <strong>${escapeHtml(run.winnerMaskedContact || maskContact(existingWinner.contact))}</strong>
          </div>
          ${run.winnerMaskedName ? `<div style="font-size: var(--font-size-base); margin-bottom: var(--space-sm); opacity: 0.9;">${escapeHtml(run.winnerMaskedName)}</div>` : ''}
          <div style="font-size: var(--font-size-sm); opacity: 0.9;">
            Data revendicării: ${(() => {
              let claimDateStr = '—';
              const dateToConvert = run.winnerClaimDate || (existingWinner?.date);
              if (dateToConvert) {
                try {
                  if (dateToConvert && typeof dateToConvert.toDate === 'function') {
                    claimDateStr = dateToConvert.toDate().toLocaleString('ro-RO');
                  } else if (dateToConvert && typeof dateToConvert.toMillis === 'function') {
                    claimDateStr = new Date(dateToConvert.toMillis()).toLocaleString('ro-RO');
                  } else if (dateToConvert.seconds) {
                    claimDateStr = new Date(dateToConvert.seconds * 1000).toLocaleString('ro-RO');
                  } else {
                    const dateObj = new Date(dateToConvert);
                    if (!isNaN(dateObj.getTime())) {
                      claimDateStr = dateObj.toLocaleString('ro-RO');
                    }
                  }
                } catch (e) {
                  logger.error('[openWinnerDraw] Error converting winner claim date:', e, dateToConvert);
                  claimDateStr = '—';
                }
              }
              return claimDateStr;
            })()}
          </div>
          ${run.winnerPrize || existingWinner.prize ? `<div style="font-size: var(--font-size-sm); opacity: 0.9; margin-top: var(--space-xs);">Recompensă: ${escapeHtml(run.winnerPrize || existingWinner.prize)}</div>` : ''}
          <div style="font-size: var(--font-size-xs); opacity: 0.8; margin-top: var(--space-md); padding-top: var(--space-md); border-top: 1px solid rgba(255,255,255,0.2);">
            Extras la: ${selectedAt}
          </div>
        </div>
      `;
    } else if (run.winners && Array.isArray(run.winners) && run.winners.length > 0) {
      // Show 3 winners if they exist
      const selectedAt = run.winnersSelectedAt ? new Date(run.winnersSelectedAt).toLocaleString('ro-RO') : '—';
      resultHtml = `
        <div class="card" style="background: linear-gradient(135deg, #1f8f4a, #1e7f45); color: white; padding: var(--space-xl); text-align: center; margin-bottom: var(--space-lg);">
          <h3 style="margin-bottom: var(--space-md); color: white;">🎉 Câștigătorii au fost extrași!</h3>
          ${run.winners.map((winner, idx) => {
            const place = idx + 1;
            const placeEmoji = place === 1 ? '🥇' : place === 2 ? '🥈' : '🥉';
            return `
              <div style="margin-bottom: ${idx < run.winners.length - 1 ? 'var(--space-lg)' : '0'}; padding-bottom: ${idx < run.winners.length - 1 ? 'var(--space-lg)' : '0'}; border-bottom: ${idx < run.winners.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none'};">
                <div style="font-size: var(--font-size-base); margin-bottom: var(--space-xs); opacity: 0.9;">
                  ${placeEmoji} Locul ${place}
                </div>
                <div style="font-size: var(--font-size-lg); margin-bottom: var(--space-sm);">
                  <strong>${escapeHtml(winner.maskedContact || '—')}</strong>
                </div>
                ${winner.maskedName ? `<div style="font-size: var(--font-size-base); margin-bottom: var(--space-xs); opacity: 0.9;">${escapeHtml(winner.maskedName)}</div>` : ''}
                ${winner.prize ? `<div style="font-size: var(--font-size-sm); opacity: 0.9; margin-top: var(--space-xs);">Recompensă: ${escapeHtml(winner.prize)}</div>` : ''}
              </div>
            `;
          }).join('')}
          <div style="font-size: var(--font-size-xs); opacity: 0.8; margin-top: var(--space-md); padding-top: var(--space-md); border-top: 1px solid rgba(255,255,255,0.2);">
            Extrași la: ${selectedAt}
          </div>
        </div>
      `;
    }
    
    contentEl.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: var(--space-lg);">
        <div class="card">
          <h4 style="margin-bottom: var(--space-md);">Informații campanie</h4>
          <div style="font-size: var(--font-size-sm);">
            <div><strong>Perioadă:</strong> ${startDate} - ${endDate}</div>
            <div style="margin-top: var(--space-xs);"><strong>Revendicări:</strong> ${stats.claimsCount || 0}</div>
            <div style="margin-top: var(--space-xs);"><strong>Câștiguri:</strong> ${stats.winsCount || 0}</div>
            <div style="margin-top: var(--space-xs);"><strong>Câștigători eligibili:</strong> ${winners.length}</div>
          </div>
        </div>
        
        ${resultHtml}
        
        <div class="card sjw-card">
          <div class="sjw-head">
            <h4 class="sjw-title">Participanți</h4>
            <span class="sjw-chip">${allClaims.length} · date ascunse</span>
          </div>
          ${allClaims.length === 0 ?
            '<div class="smallnote" style="text-align: center; padding: var(--space-lg);">Niciun participant</div>' :
            `<ol class="sjw-list">
              ${allClaims.map((claim, i) => {
                const dt = claimDateObj(claim.date);
                const when = dt ? `${dt.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' })} · ${dt.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}` : '—';
                const extra = claim.receiptNumber ? ` · bon ${escapeHtml(String(claim.receiptNumber))}` : '';
                return `
                <li class="sjw-row">
                  <span class="sjw-nr">${i + 1}</span>
                  <span class="sjw-text">
                    <span class="sjw-line"><b>${escapeHtml(maskName(claim.name || ''))}</b><span class="sjw-contact">${escapeHtml(maskContact(claim.contact || ''))}</span></span>
                    <span class="sjw-when">${when}${extra}</span>
                  </span>
                </li>`;
              }).join('')}
            </ol>`
          }
        </div>
        
        ${!winnerSelected && !(run.winners && Array.isArray(run.winners) && run.winners.length > 0) && winners.length > 0 ? `
          <div class="card" style="text-align: center;">
            <h4 style="margin-bottom: var(--space-md);">Extragere câștigător</h4>
            <p style="margin-bottom: var(--space-lg); color: var(--text-secondary);">
              Se va extrage aleator un câștigător dintre cei ${winners.length} eligibili.
            </p>
            <button id="drawWinnerBtn" class="btn-primary" style="padding: var(--space-md) var(--space-xl); font-size: var(--font-size-base);">
              Alege aleator un câștigător
            </button>
          </div>
          
          <div class="card" style="text-align: center;">
            <h4 style="margin-bottom: var(--space-sm);">Extragere 3 câștigători (Loc 1, Loc 2, Loc 3)</h4>
            <p style="margin-bottom: var(--space-lg); color: var(--text-secondary); font-size: var(--font-size-sm);">
              Se vor extrage 3 câștigători diferiți dintre eligibili.
            </p>
            <button id="drawWinnersTop3Btn" class="btn-primary" ${winners.length < 3 ? 'disabled' : ''} style="padding: var(--space-md) var(--space-xl); font-size: var(--font-size-base);${winners.length < 3 ? ' opacity: 0.5; cursor: not-allowed;' : ''}">
              Alege aleator 3 câștigători
            </button>
            ${winners.length < 3 ? `<p style="margin-top: var(--space-sm); color: var(--text-muted); font-size: var(--font-size-xs);">Necesită minim 3 câștigători eligibili (disponibili: ${winners.length})</p>` : ''}
          </div>
        ` : ''}
        
        ${winnerSelected && !existingWinner ? `
          <div class="card">
            <div class="smallnote" style="text-align: center; padding: var(--space-lg);">
              Câștigătorul a fost deja extras, dar datele nu mai sunt disponibile.
            </div>
          </div>
        ` : ''}
        
        ${winners.length === 0 ? `
          <div class="card">
            <div class="smallnote" style="text-align: center; padding: var(--space-lg);">
              Nu există câștigători eligibili pentru extragere.
            </div>
          </div>
        ` : ''}
      </div>
    `;
    
    // Attach event listeners after DOM is inserted
    const drawWinnerBtn = document.getElementById('drawWinnerBtn');
    if (drawWinnerBtn) {
      drawWinnerBtn.addEventListener('click', () => drawWinner(runId));
    }
    
    const drawWinnersTop3Btn = document.getElementById('drawWinnersTop3Btn');
    if (drawWinnersTop3Btn) {
      drawWinnersTop3Btn.addEventListener('click', () => drawWinnersTop3(runId));
    }
  } catch (e) {
    logger.error('[openWinnerDraw] Error:', e);
    contentEl.innerHTML = archiveLoadErrorHtml(e, 'winnerDrawRetry');
    document.getElementById('winnerDrawRetry')?.addEventListener('click', () => openWinnerDraw(runId));
  }
}

// Draw winner
export async function drawWinner(runId) {
  // Protecție anti-dublu-click
  if (isDrawingWinner) return;
  
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }
  
  // Găsește butonul și textul original
  const btn = document.getElementById('drawWinnerBtn');
  const originalText = btn ? btn.textContent : 'Alege aleator un câștigător';
  
  // Setează flag și dezactivează butonul
  isDrawingWinner = true;
  if (btn) {
    btn.disabled = true;
    btn.style.pointerEvents = 'none';
    btn.classList.add('btn-disabled-state');
    btn.textContent = 'Extragere în curs...';
  }
  
  try {
    const loaded = await loadArchivedRun(currentUser.uid, runId);
    
    if (!loaded.found) {
      if (btn) {
        btn.disabled = false;
        btn.style.pointerEvents = '';
        btn.classList.remove('btn-disabled-state');
        btn.textContent = originalText;
      }
      isDrawingWinner = false;
      return showSnackbar('Campania nu a fost găsită');
    }
    
    const run = loaded.run;
    
    // Check if winner already selected
    if (run.winnerSelected === true) {
      if (btn) {
        btn.disabled = false;
        btn.style.pointerEvents = '';
        btn.classList.remove('btn-disabled-state');
        btn.textContent = originalText;
      }
      isDrawingWinner = false;
      return showSnackbar('Câștigătorul a fost deja extras. Extragerea este blocată.');
    }
    
    // Check if Top 3 winners have already been drawn
    if (run.winners && Array.isArray(run.winners) && run.winners.length > 0) {
      if (btn) {
        btn.disabled = false;
        btn.style.pointerEvents = '';
        btn.classList.remove('btn-disabled-state');
        btn.textContent = originalText;
      }
      isDrawingWinner = false;
      return showSnackbar('Câștigătorii au fost deja extrași (Top 3). Extragerea unui singur câștigător este blocată.');
    }
    
    // Load winners
    const allClaims = loaded.claims;
    
    const defaultPrizeMsg = run.snapshotSettings?.defaultPrizeMsg || 'Ne pare rău, nu ai câștigat!';
    const winners = allClaims.filter(claim => {
      return claim.prize && claim.prize !== defaultPrizeMsg && claim.prize.trim() !== '';
    });
    
    if (winners.length === 0) {
      if (btn) {
        btn.disabled = false;
        btn.style.pointerEvents = '';
        btn.classList.remove('btn-disabled-state');
        btn.textContent = originalText;
      }
      isDrawingWinner = false;
      return showSnackbar('Nu există câștigători eligibili');
    }
    
    // Select random winner using crypto.getRandomValues if available, else Math.random()
    let randomIndex;
    if (window.crypto && window.crypto.getRandomValues) {
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      randomIndex = array[0] % winners.length;
    } else {
      randomIndex = Math.floor(Math.random() * winners.length);
    }
    
    // Start backend draw in parallel with countdown so result appears immediately after 10s
    const drawPromise = drawWinnerBackend(runId);
    await runDrawCountdown(DRAW_COUNTDOWN_SECONDS);
    await drawPromise;
    
    // Reload modal (butonul va fi regenerat cu starea corectă)
    await openWinnerDraw(runId);
  } catch (e) {
    logger.error('[drawWinner] Error:', e);
    showSnackbar('Eroare la extragere: ' + e.message);
    
    // La eroare: reactivează butonul și resetează textul
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
  } finally {
    // Resetează flag-ul
    isDrawingWinner = false;
  }
}

// Draw 3 winners (Top 3)
export async function drawWinnersTop3(runId) {
  // Protecție anti-dublu-click
  if (isDrawingWinnersTop3) return;
  
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return showSnackbar('Nu ești logat');
  }
  
  // Găsește butonul și textul original
  const btn = document.getElementById('drawWinnersTop3Btn');
  const originalText = btn ? btn.textContent : 'Alege aleator 3 câștigători';
  
  // Setează flag și dezactivează butonul
  isDrawingWinnersTop3 = true;
  if (btn) {
    btn.disabled = true;
    btn.style.pointerEvents = 'none';
    btn.classList.add('btn-disabled-state');
    btn.textContent = 'Extragere în curs...';
  }
  
  try {
    // Start backend draw in parallel with countdown so result appears immediately after 10s
    const drawPromise = drawWinnersTop3Backend(runId);
    await runDrawCountdown(DRAW_COUNTDOWN_SECONDS);
    await drawPromise;
    
    // Reload modal (butonul va fi regenerat cu starea corectă)
    await openWinnerDraw(runId);
  } catch (e) {
    logger.error('[drawWinnersTop3] Error:', e);
    showSnackbar('Eroare la extragere: ' + e.message);
    
    // La eroare: reactivează butonul și resetează textul
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.classList.remove('btn-disabled-state');
      btn.textContent = originalText;
    }
  } finally {
    // Resetează flag-ul
    isDrawingWinnersTop3 = false;
  }
}

export function closeWinnerDraw() {
  const modal = document.getElementById('winnerDrawModal');
  if (modal) modal.classList.remove('active');
}

// ✅ Render recent claims list (top 2-3 items) - email in clear text
function renderRecentClaims(rows) {
  const container = document.getElementById('dashboardRecentClaimsList');
  if (!container) return;

  // ✅ FIX: Clear container using DOM API (safer than innerHTML)
  container.replaceChildren();

  if (!rows || rows.length === 0) {
    const emptyMsg = document.createElement('div');
    emptyMsg.className = 'dashboard-empty-state';
    emptyMsg.textContent = 'Încă nu există revendicări înregistrate.';
    container.appendChild(emptyMsg);
    return;
  }

  // Take top 3 claims
  const recent = rows.slice(0, 3);
  
  // ✅ FIX: Use DOM API instead of innerHTML template string
  recent.forEach((claim) => {
    // Get initials for avatar
    const name = claim.name || '';
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
    
    // ✅ Format date/time on ONE line: DD.MM.YYYY · HH:mm
    let dateTimeStr = '—';
    if (claim.date) {
      try {
        let claimDate = null;
        if (claim.date && typeof claim.date.toDate === 'function') {
          claimDate = claim.date.toDate();
        } else if (claim.date && typeof claim.date.toMillis === 'function') {
          claimDate = new Date(claim.date.toMillis());
        } else if (claim.date.seconds) {
          claimDate = new Date(claim.date.seconds * 1000);
        } else {
          claimDate = new Date(claim.date);
        }
        if (!isNaN(claimDate.getTime())) {
          const day = String(claimDate.getDate()).padStart(2, '0');
          const month = String(claimDate.getMonth() + 1).padStart(2, '0');
          const year = claimDate.getFullYear();
          const hours = String(claimDate.getHours()).padStart(2, '0');
          const minutes = String(claimDate.getMinutes()).padStart(2, '0');
          dateTimeStr = `${day}.${month}.${year} · ${hours}:${minutes}`;
        }
      } catch (e) {
        logger.warn('[renderRecentClaims] Error parsing date:', e);
      }
    }

    // ✅ Show email in clear text (not masked)
    const contact = claim.contact || '';

    // ✅ FIX: Create DOM elements safely (no innerHTML with user data)
    const item = document.createElement('div');
    item.className = 'dashboard-claim-item';
    
    const avatar = document.createElement('div');
    avatar.className = 'dashboard-claim-avatar';
    avatar.textContent = initials; // Safe: calculated from name
    
    const info = document.createElement('div');
    info.className = 'dashboard-claim-info';
    
    const nameDiv = document.createElement('div');
    nameDiv.className = 'dashboard-claim-name';
    nameDiv.textContent = name || ''; // Safe: textContent auto-escapes
    
    const emailDiv = document.createElement('div');
    emailDiv.className = 'dashboard-claim-email';
    emailDiv.textContent = contact || ''; // Safe: textContent auto-escapes

    info.appendChild(nameDiv);
    info.appendChild(emailDiv);
    if (claim.receiptNumber) {
      const bonDiv = document.createElement('div');
      bonDiv.className = 'smallnote';
      bonDiv.style.marginTop = '4px';
      bonDiv.style.color = 'var(--text-secondary)';
      bonDiv.textContent = `Număr bon: ${claim.receiptNumber}`;
      info.appendChild(bonDiv);
    }
    
    const timeDiv = document.createElement('div');
    timeDiv.className = 'dashboard-claim-time';
    timeDiv.textContent = dateTimeStr; // Safe: formatted date string
    
    item.appendChild(avatar);
    item.appendChild(info);
    item.appendChild(timeDiv);
    container.appendChild(item);
  });
}

function renderRecentCommunityLeads(rows) {
  const container = document.getElementById('dashboardCommunityLeadsList');
  if (!container) return;
  container.replaceChildren();

  if (!rows || rows.length === 0) {
    const emptyMsg = document.createElement('div');
    emptyMsg.className = 'dashboard-empty-state';
    emptyMsg.textContent = 'Încă nu există înscrieri în comunitate.';
    container.appendChild(emptyMsg);
    return;
  }

  rows.slice(0, 3).forEach((lead) => {
    const contact = String(lead.contact || '').trim() || '—';
    const initials = contact.slice(0, 2).toUpperCase();
    const dateTimeStr = formatFirestoreDateShort(lead.createdAt || lead.date);
    const typeLabel = lead.contactType === 'phone' ? 'Telefon' : 'Email';

    const item = document.createElement('div');
    item.className = 'dashboard-claim-item';

    const avatar = document.createElement('div');
    avatar.className = 'dashboard-claim-avatar';
    avatar.textContent = initials;

    const info = document.createElement('div');
    info.className = 'dashboard-claim-info';

    const nameDiv = document.createElement('div');
    nameDiv.className = 'dashboard-claim-name';
    nameDiv.textContent = contact;

    const statusDiv = document.createElement('div');
    statusDiv.className = 'dashboard-claim-email';
    statusDiv.textContent = `${typeLabel} · S-a alăturat`;

    info.appendChild(nameDiv);
    info.appendChild(statusDiv);

    const timeDiv = document.createElement('div');
    timeDiv.className = 'dashboard-claim-time';
    timeDiv.textContent = dateTimeStr;

    item.appendChild(avatar);
    item.appendChild(info);
    item.appendChild(timeDiv);
    container.appendChild(item);
  });
}

// ✅ Open modal with all claims
export async function openAllClaimsModal() {
  const modal = document.getElementById('allClaimsModal');
  if (!modal) return;
  const cs = getCurrentSettings();
  const draw = !!cs && cs.campaignKind === 'tombola' && Array.isArray(cs.drawPrizes) && cs.drawPrizes.length > 0;
  const h = document.getElementById('allClaimsTitle');
  if (h) h.textContent = draw ? 'Toate înscrierile' : 'Toate revendicările';
  openClaimId = null;

  modal.classList.add('active');

  // "Vezi toate revendicările" should show only current campaign claims.
  // Archived winners remain available in the closed campaigns views.
  cachedWinnersData = null;
  renderAllClaimsInModal(lastClaimsRows || []);
}

// ✅ Close modal
export function closeAllClaimsModal() {
  const modal = document.getElementById('allClaimsModal');
  if (modal) modal.classList.remove('active');
}

export function openCommunityLeadsModal() {
  const modal = document.getElementById('communityLeadsModal');
  if (!modal) return;
  const chips = document.getElementById('communityLeadsFilters');
  if (chips && chips.dataset.bound !== '1') {
    chips.dataset.bound = '1';
    chips.addEventListener('click', (e) => {
      const b = e.target.closest('[data-filter]');
      if (!b) return;
      communityFilter = b.dataset.filter;
      renderCommunityLeadsInModal();
    });
  }
  communityFilter = 'all';
  modal.classList.add('active');
  renderCommunityLeadsInModal();
  loadMarketingAudience(true);
}

export function closeCommunityLeadsModal() {
  const modal = document.getElementById('communityLeadsModal');
  if (modal) modal.classList.remove('active');
}

let communityFilter = 'all';
let marketingAudience = null; // [{ contact, type, sources, lastAt }]
let marketingAudienceLoading = false;
let marketingAudienceError = null;

export function getMarketingAudienceCache() {
  return marketingAudience;
}

/** Încarcă lista de marketing de pe server (toate sursele, fără dubluri). */
export async function loadMarketingAudience(force = false) {
  if (marketingAudienceLoading) return marketingAudience;
  if (marketingAudience && !force) return marketingAudience;
  marketingAudienceLoading = true;
  marketingAudienceError = null;
  try {
    const { listMarketingAudienceBackend } = await import('./backend.js?v=82.86');
    const r = await listMarketingAudienceBackend();
    marketingAudience = Array.isArray(r?.audience) ? r.audience : [];
  } catch (e) {
    logger.warn('[loadMarketingAudience]', e);
    marketingAudienceError = e;
  } finally {
    marketingAudienceLoading = false;
  }
  try { document.dispatchEvent(new CustomEvent('sj:claims-updated')); } catch (_) { /* ignore */ }
  if (document.getElementById('communityLeadsModal')?.classList.contains('active')) renderCommunityLeadsInModal();
  return marketingAudience;
}

export async function refreshMarketingAudience() {
  await loadMarketingAudience(true);
}

/** Până vine lista de pe server: doar înscrierile din comunitate (în timp real). */
function audienceFallbackFromLeads(rows) {
  const seen = new Set();
  const out = [];
  (rows || []).forEach((r) => {
    const contact = String(r.contact || '').trim();
    if (!contact) return;
    const key = contact.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const dt = claimDateObj(r.createdAt || r.date);
    out.push({ contact, type: r.contactType === 'phone' ? 'phone' : 'email', sources: ['Comunitate'], lastAt: dt ? dt.toISOString() : null });
  });
  return out;
}

export function getAudienceForDisplay() {
  return marketingAudience || audienceFallbackFromLeads(lastCommunityLeadsRows);
}

/** Comunitate = toți cei care au acceptat oferte: listă pe zile, sursa lângă fiecare, Scrie / Sună. */
function renderCommunityLeadsInModal() {
  const q = (document.getElementById('communityLeadsSearch')?.value || '').toLowerCase().trim();
  const list = document.getElementById('communityLeadsList');
  if (!list) return;
  const all = getAudienceForDisplay();
  const isPhone = (r) => r.type === 'phone';
  const phones = all.filter(isPhone).length;
  const emails = all.length - phones;
  const sub = document.getElementById('communityLeadsSub');
  if (sub) {
    sub.textContent = all.length
      ? `${all.length} ${all.length === 1 ? 'contact' : 'contacte'} cu acord pentru oferte${marketingAudienceLoading ? ' · se actualizează…' : ''}`
      : (marketingAudienceLoading ? 'Se încarcă…' : '');
  }
  const chips = document.getElementById('communityLeadsFilters');
  if (chips) {
    chips.hidden = !(phones && emails);
    const labels = { all: `Toți · ${all.length}`, email: `E-mail · ${emails}`, phone: `Telefon · ${phones}` };
    chips.querySelectorAll('[data-filter]').forEach((b) => {
      b.textContent = labels[b.dataset.filter];
      const on = b.dataset.filter === communityFilter;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
  const sheet = document.querySelector('#communityLeadsModal .sjc-sheet');
  if (sheet) sheet.classList.toggle('is-compact', !all.length);
  const actions = document.querySelector('#communityLeadsModal .sjc-actions');
  if (actions) actions.hidden = !all.length;
  const copyBtn = document.getElementById('communityCopyEmailsBtn');
  if (copyBtn) copyBtn.hidden = !emails;
  const search = document.querySelector('#communityLeadsModal .sjc-search');
  if (search) search.hidden = all.length < 6;

  list.replaceChildren();
  if (!all.length) {
    const empty = sjcEl('div', 'sjl-empty');
    empty.innerHTML = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/><path d="M16 4.5a3.5 3.5 0 0 1 0 7M21.5 20a6.5 6.5 0 0 0-4-6"/></svg>';
    if (marketingAudienceError && !marketingAudience) {
      empty.appendChild(sjcEl('b', null, 'Nu am putut încărca lista'));
      empty.appendChild(sjcEl('span', null, 'Apasă reîncarcă (sus) ca să încerci din nou.'));
    } else {
      empty.appendChild(sjcEl('b', null, marketingAudienceLoading ? 'Se încarcă…' : 'Încă nu a acceptat nimeni oferte'));
      empty.appendChild(sjcEl('span', null, 'Aici apar toți cei care acceptă să primească oferte: din comunitate, campanii și fidelizare.'));
    }
    list.appendChild(empty);
    return;
  }

  const box = sjcEl('div', 'sjc-list sjc-list--static');
  let lastDay = null;
  let shown = 0;
  all.forEach((r) => {
    if (communityFilter === 'email' && isPhone(r)) return;
    if (communityFilter === 'phone' && !isPhone(r)) return;
    const dt = r.lastAt ? new Date(r.lastAt) : null;
    const contact = String(r.contact || '').trim();
    const sources = Array.isArray(r.sources) ? r.sources : [];
    if (q && !`${contact} ${sources.join(' ')}`.toLowerCase().includes(q)) return;
    shown++;
    const day = claimDayLabel(dt && !isNaN(dt.getTime()) ? dt : null);
    if (day !== lastDay) { box.appendChild(sjcEl('div', 'sjc-day', day)); lastDay = day; }
    const row = sjcEl('div', 'sjc-item sjk-crow');
    const av = sjcEl('span', `sjc-av${day === 'Azi' ? '' : ' sjc-av--old'}`);
    av.innerHTML = isPhone(r)
      ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z"/></svg>'
      : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>';
    row.appendChild(av);
    const txt = sjcEl('span', 'sjc-row__text');
    txt.appendChild(sjcEl('b', 'sjc-row__contact sjk-ccontact', contact || '—'));
    const tags = sjcEl('span', 'sjc-tags');
    sources.slice(0, 3).forEach((src) => tags.appendChild(sjcEl('span', `sjc-tag${src === 'Comunitate' ? '' : ' sjc-tag--grey'}`, src)));
    if (sources.length > 3) tags.appendChild(sjcEl('span', 'sjc-tag sjc-tag--grey', `+${sources.length - 3}`));
    txt.appendChild(tags);
    row.appendChild(txt);
    if (contact) {
      const a = sjcEl('a', 'sjk-cact');
      a.href = isPhone(r) ? `sms:${contact.replace(/[^\d+]/g, '')}` : `mailto:${contact}`;
      a.textContent = isPhone(r) ? 'SMS' : 'Scrie';
      row.appendChild(a);
    }
    box.appendChild(row);
  });
  if (!shown) box.appendChild(sjcEl('p', 'sjc-empty', 'Nimic găsit.'));
  list.appendChild(box);
}

/** Copiază toate e-mailurile (pentru BCC în Gmail / Outlook). */
export async function copyMarketingEmails() {
  const emails = getAudienceForDisplay().filter((r) => r.type === 'email').map((r) => r.contact);
  if (!emails.length) return showSnackbar('Nu ai încă adrese de e-mail');
  const text = emails.join(', ');
  try {
    await navigator.clipboard.writeText(text);
  } catch (_) {
    const ta = document.createElement('textarea');
    ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (__) { /* ignore */ }
    ta.remove();
  }
  showSnackbar(`${emails.length} ${emails.length === 1 ? 'adresă copiată' : 'adrese copiate'}. Pune-le la „BCC”, nu la „Către”.`, 'success');
}

// ✅ Render all claims table in modal
function claimDateObj(d) {
  if (!d) return null;
  try {
    if (typeof d.toDate === 'function') return d.toDate();
    if (typeof d.toMillis === 'function') return new Date(d.toMillis());
    if (d.seconds) return new Date(d.seconds * 1000);
    const o = new Date(d);
    return isNaN(o.getTime()) ? null : o;
  } catch (_) { return null; }
}

function isDrawCampaignSettings(cs) {
  return !!cs && cs.campaignKind === 'tombola' && Array.isArray(cs.drawPrizes) && cs.drawPrizes.length > 0;
}

function claimDayLabel(dt) {
  if (!dt) return 'Fără dată';
  const day = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (day === today) return 'Azi';
  if (day === today - 864e5) return 'Ieri';
  return dt.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
}

function claimInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function sjcEl(tag, cls, text) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

let openClaimId = null;

/** Lista de înscrieri/revendicări pe zile; un rând se deschide cu toate detaliile.
 *  Folosită în „Toate înscrierile” și în „Campanie arhivată”. Întoarce câte rânduri s-au afișat. */
function renderClaimCards(list, rows, { q = '', draw = false, getOpen, setOpen, rerender, emptyText }) {
  const total = rows.length;
  list.innerHTML = '';
  let shown = 0;
  let lastDay = null;
  rows.forEach((r, i) => {
    const dt = claimDateObj(r.date);
    const nr = total - i;
    const dateStr = dt ? dt.toLocaleString('ro-RO') : '—';
    const hay = (dateStr + ' ' + (r.name || '') + ' ' + (r.contact || '') + ' ' + (r.prize || '') + ' ' + (r.voucherCode || '') + ' ' + (r.receiptNumber || '') + ' ' + (r.gdprAccepted ? 'gdpr' : '') + ' ' + (r.marketingAccepted ? 'mkt marketing' : '') + ' ' + (r.responsibleName || '')).toLowerCase();
    if (q && !hay.includes(q)) return;
    shown++;

    const day = claimDayLabel(dt);
    if (day !== lastDay) {
      list.appendChild(sjcEl('div', 'sjc-day', day));
      lastDay = day;
    }

    const id = r._id || r.id || `${nr}`;
    const isOpen = getOpen() === id;
    const item = sjcEl('div', `sjc-item${isOpen ? ' is-open' : ''}`);
    const row = sjcEl('button', 'sjc-row');
    row.type = 'button';
    row.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    row.appendChild(sjcEl('span', `sjc-av${day === 'Azi' ? '' : ' sjc-av--old'}`, claimInitials(r.name)));
    const txt = sjcEl('span', 'sjc-row__text');
    const top = sjcEl('span', 'sjc-row__top');
    top.appendChild(sjcEl('b', 'sjc-row__name', r.name || '—'));
    top.appendChild(sjcEl('span', 'sjc-row__time', dt ? dt.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : ''));
    txt.appendChild(top);
    txt.appendChild(sjcEl('span', 'sjc-row__contact', r.contact || '—'));
    const tags = sjcEl('span', 'sjc-tags');
    if (r.gdprAccepted) tags.appendChild(sjcEl('span', 'sjc-tag', 'GDPR'));
    const mk = r?.consent?.marketing;
    if ((mk && (mk.email || mk.sms)) || (!mk && r.marketingAccepted)) tags.appendChild(sjcEl('span', 'sjc-tag', 'Marketing'));
    if (r.receiptNumber) tags.appendChild(sjcEl('span', 'sjc-tag sjc-tag--grey', `Bon ${r.receiptNumber}`));
    if (!draw && r.prize) tags.appendChild(sjcEl('span', 'sjc-tag sjc-tag--grey', r.prize));
    if (r.voucherCode && r.redeemedAt) tags.appendChild(sjcEl('span', 'sjc-tag sjc-tag--grey', 'Cod folosit'));
    if (tags.childNodes.length) txt.appendChild(tags);
    row.appendChild(txt);
    row.addEventListener('click', () => {
      setOpen(isOpen ? null : id);
      rerender();
    });
    item.appendChild(row);

    if (isOpen) {
      const det = sjcEl('div', 'sjc-detail');
      const grid = sjcEl('div', 'sjc-grid');
      const field = (label, value, wide) => {
        const f = sjcEl('div', `sjc-field${wide ? ' sjc-field--wide' : ''}`);
        f.appendChild(sjcEl('span', 'sjc-field__label', label));
        if (value instanceof Node) f.appendChild(value); else f.appendChild(sjcEl('span', 'sjc-field__value', value || '—'));
        return f;
      };
      grid.appendChild(field('Data', `${dateStr} · nr. ${nr}`, true));
      grid.appendChild(field(draw ? 'Tip' : 'Recompensă', `${r.prize || '—'}${r.voucherCode ? ` · cod ${r.voucherCode}${r.redeemedAt ? ' (folosit)' : ''}` : ''}`, true));
      grid.appendChild(field('Număr bon', r.receiptNumber || '—'));
      grid.appendChild(field('GDPR', r.gdprAccepted ? 'Acceptat' : 'Nu'));
      const mkCell = marketingCell(r, (t) => sjcEl('span', 'sjc-field__value', t || 'Nu'));
      grid.appendChild(field('Marketing', mkCell));
      grid.appendChild(field('Responsabil', r.responsibleName || '—'));
      det.appendChild(grid);

      const acts = sjcEl('div', 'sjc-detail__acts');
      const contact = String(r.contact || '').trim();
      if (contact) {
        const isMail = contact.includes('@');
        const a = sjcEl('a', 'sjc-mini', isMail ? 'Scrie e-mail' : 'Sună');
        a.href = isMail ? `mailto:${contact}` : `tel:${contact.replace(/[^\d+]/g, '')}`;
        acts.appendChild(a);
        const c = sjcEl('button', 'sjc-mini', 'Copiază contactul');
        c.type = 'button';
        c.addEventListener('click', async () => {
          try { await navigator.clipboard.writeText(contact); showSnackbar('Contact copiat', 'success'); } catch (_) { showSnackbar('Nu am putut copia'); }
        });
        acts.appendChild(c);
      }
      det.appendChild(acts);
      item.appendChild(det);
    }
    list.appendChild(item);
  });

  if (!shown) list.appendChild(sjcEl('p', 'sjc-empty', q ? 'Nimic găsit pentru căutarea asta.' : emptyText));
  return shown;
}

/** „Toate înscrierile / revendicările” (campania curentă). */
function renderAllClaimsInModal(rows) {
  const list = document.getElementById('allClaimsList');
  if (!list) return;
  const draw = isDrawCampaignSettings(getCurrentSettings());
  const todayCount = rows.filter((r) => claimDayLabel(claimDateObj(r.date)) === 'Azi').length;
  const sub = document.getElementById('allClaimsSub');
  if (sub) sub.textContent = rows.length ? `${rows.length} la campania curentă · ${todayCount} azi` : '';
  renderClaimCards(list, rows, {
    q: (document.getElementById('allClaimsSearch')?.value || '').toLowerCase().trim(),
    draw,
    getOpen: () => openClaimId,
    setOpen: (v) => { openClaimId = v; },
    rerender: () => renderAllClaimsInModal(lastClaimsRows || []),
    emptyText: draw ? 'Încă nu s-a înscris nimeni.' : 'Încă nu a revendicat nimeni un premiu.'
  });
}

// ✅ Search listener for modal
document.addEventListener('input', (e) => {
  if (e.target && e.target.id === 'allClaimsSearch') {
    renderAllClaimsInModal(lastClaimsRows || []);
  }
  if (e.target && e.target.id === 'communityLeadsSearch') {
    renderCommunityLeadsInModal();
  }
});

// Make available globally for onclick handlers
window.refreshClaims = refreshClaims;
window.submitClaim = submitClaim;
window.exportClaims = exportClaims;
window.exportCommunityLeads = exportCommunityLeads;
window.resetClaims = resetClaims;
window.loadArchivedCampaigns = loadArchivedCampaigns;
window.viewArchivedCampaign = viewArchivedCampaign;
window.closeArchivedCampaignView = closeArchivedCampaignView;
window.exportArchivedCampaignClaims = exportArchivedCampaignClaims;
window.openWinnerDraw = openWinnerDraw;

/** Răzuiește: descarcă lista biletelor + cheia de verificare (pentru participanții care o cer). */
export async function downloadScratchDeck(runId) {
  try {
    const { exportScratchDeckBackend } = await import('./backend.js?v=82.86');
    const d = await exportScratchDeckBackend(runId);
    const name = (i) => (i < 0 ? 'Necâștigător' : (d.prizes[i] && d.prizes[i].message) || `Premiul ${i + 1}`);
    // celulă CSV sigură: ghilimele dublate, fără formule Excel (=, +, -, @ la început)
    const cell = (v) => {
      let t = String(v == null ? '' : v).replace(/\r?\n/g, ' ');
      if (/^[=+\-@]/.test(t)) t = `'${t}`;
      return /[";\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    const lines = [
      'Lista biletelor - Răzuiește și câștigă',
      `Campanie;${cell(d.runId)}`,
      `Blocată la;${cell(d.lockedAt || '-')}`,
      `Amprenta SHA-256 publicată;${cell(d.hash)}`,
      `Cheie de verificare (sare);${cell(d.salt)}`,
      `Verificare amprentă;${d.hashOk ? 'CORECTĂ' : 'NU CORESPUNDE'}`,
      `Bilete distribuite;${d.distributed} din ${d.tickets.length}`,
      '',
      'Cum se verifică: SHA-256 din textul  <sare>:<lista>  unde <lista> este rândul JSON de mai jos.',
      `JSON;${cell(JSON.stringify(d.tickets))}`,
      `Coduri premii;${cell(`-1 = Necâștigător; ${d.prizes.map((p) => `${p.index} = ${p.message}`).join('; ')}`)}`,
      '',
      'Nr. bilet;Rezultat;Distribuit',
      ...d.tickets.map((t, i) => `${i + 1};${cell(name(t))};${i < d.distributed ? 'da' : 'nu'}`)
    ];
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `lista-bilete-${String(d.runId).slice(0, 24)}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1500);
  } catch (e) {
    try { (await import('../utils/ui.js?v=82.86')).showSnackbar(e?.message || 'Nu am putut descărca lista.', 'error'); } catch (_) { alert(e?.message || 'Eroare'); }
  }
}
window.downloadScratchDeck = downloadScratchDeck;
window.openAllClaimsModal = openAllClaimsModal;
window.closeAllClaimsModal = closeAllClaimsModal;
window.openCommunityLeadsModal = openCommunityLeadsModal;
window.copyMarketingEmails = copyMarketingEmails;
window.refreshMarketingAudience = refreshMarketingAudience;
window.closeCommunityLeadsModal = closeCommunityLeadsModal;
window.openClosedCampaignsModal = openClosedCampaignsModal;
window.closeClosedCampaignsModal = closeClosedCampaignsModal;
window.updateClosedCampaignsBadge = updateClosedCampaignsBadge;
window.closeWinnerDraw = closeWinnerDraw;
window.drawWinner = drawWinner;
window.drawWinnersTop3 = drawWinnersTop3;
window.openDeleteArchivedCampaignModal = openDeleteArchivedCampaignModal;
window.closeDeleteArchivedCampaignModal = closeDeleteArchivedCampaignModal;
window.confirmDeleteArchivedCampaign = confirmDeleteArchivedCampaign;
window.openDeleteAllClaimsModal = openDeleteAllClaimsModal;
window.closeDeleteAllClaimsModal = closeDeleteAllClaimsModal;
window.confirmDeleteAllClaims = confirmDeleteAllClaims;
